import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback
} from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import './EditMilestoneModal.css';
import LDAPSearchField from '../../../app/components/shared/LDAPSearch/LDAPSearchField';
import BlackCheckMarkIcon from '../../../app/assets/svgIcons/BlackCheckMarkIcon';
import CalendarIcon from '@spectrum-icons/workflow/Calendar';
import { DialogTrigger, Dialog } from '@adobe/react-spectrum';
import { CalendarRange } from '../../../app/components/Fields/Calendar';
import { parseDate, CalendarDate } from '@internationalized/date';
import { DateComponent } from '../../../app/components/RsdForms/CreateMoment/components/DateComponent';
// Using native input[type=time] for robust overlay behavior across nested portals
// Removed Spectrum TimeField usage; using native input[type="time"] inline
import {
  toUtcIsoFromLocalParts,
  formatUtcTimeRange,
  formatLocalTimeRange,
  utcLabelTimeToLocal24,
  getTimezoneAbbreviation,
  storeWithTimezone,
  convertToViewerTimezone,
  parseStoredMilestoneTime,
  getYyyyMmDdInTimeZone,
  addBusinessDaysIso
} from '../../../utils/time';
import {
  ENHANCED_MILESTONE_STATUSES as MILESTONE_STATUSES,
  ENHANCED_STATUS_MENUS as STATUS_MENUS,
  // getEnhancedVisibleFields IS getEnhancedEditFields (alias). It accepts milestoneName
  // and applies MILESTONE_NAME_OVERRIDES, so hiddenFields reflects name-based overrides.
  getEnhancedEditFields as getEnhancedVisibleFields,
  getMilestoneApprovalRequired,
  getMilestoneExcludedReviewTypes,
  getMilestoneDocLinkPolicy,
  getEnhancedStatusOptions as getStatusOptions,
  getEnhancedCellStyling as getCellStyling,
  formatEnhancedCellDisplay as formatMilestoneDisplayText,
  getEnhancedDynamicFieldLabel,
  getExcelHoverData,
  MILESTONE_ICONS,
  validateEnhancedMilestoneData,
  validateDateRelationship,
  MILESTONE_FORM_FIELDS,
  getVisibleFields as getLegacyVisibleFields,
  getDynamicFieldLabel,
  getMilestoneStatusLabel
} from '../../../app/components/SlideOutPanel/Milestones/milestoneConfigEnhanced';
import { validateMilestoneLinkField } from '../../../app/components/Form/validators';
import {
  fetchDefaultApprovers,
  type SlcTeamContact
} from '../../../app/services/momentsApi';
import { getCookie } from '../../../utils/helpers';
import {
  isSameUser,
  stripRequestorFromDefaults
} from '../../../utils/excludeUser';
import { selectUserProfile } from '../../../app/pages/Layout/slice/selectors';
import {
  buildSlcApprovalSubject,
  type SlcApprovalSubjectContext
} from '../../../utils/slcApprovalSubject';
import {
  MILESTONE_REVIEW_TYPE,
  SEND_TARGET_DEFAULT_DATE_HELPER_MESSAGE
} from '../../../utils/constants';
import { mapMilestoneDateFields } from '../../../app/components/SlideOutPanel/Milestones/milestoneDateFieldMapper';
import { shouldShowSendTargetDefaultDateHelper } from './editMilestoneModalUtils';
import SendForApprovalForm, {
  shouldShowSendForApprovalForm
} from './SendForApprovalForm';
import ApprovalFlowSteps from './ApprovalFlowSteps';
import {
  computeApprovalFlowStage,
  parseCcContacts,
  type ApprovalCcContact,
  ApprovalFlowStage,
  isAsyncSentSendForApprovalAttempt,
  shouldClearSendForApprovalOnManualStatusChange,
  isNotApprovedStatus,
  canReopenStatusField as resolveCanReopenStatusField,
  resolveStatusDropdownOption,
  getNewApprovalCycleState,
  isInactiveApprovalReviewType
} from '../../../utils/approvalFlow';

/**
 * Returns true when the given status renders the approver field as required in the edit form.
 * Used to decide whether to trigger a default-approver API fetch on status change.
 */
function statusRequiresApproverField(statusId: string): boolean {
  const entry = Object.values(MILESTONE_STATUSES).find(
    (s: any) => s.id === statusId
  ) as any;
  if (!entry?.editFieldRequirements) return false;
  const required: string[] = entry.editFieldRequirements.required ?? [];
  const hidden: string[] = entry.editFieldRequirements.hidden ?? [];
  return required.includes('approver') && !hidden.includes('approver');
}

function isApproverEmpty(approver: any): boolean {
  if (!approver) return true;
  if (Array.isArray(approver)) return approver.length === 0;
  if (typeof approver === 'string') return approver.trim() === '';
  return false;
}

/** Primary "Approved" statuses that map primary date → approvedOn (excludes re-approved flows). */
function isSimpleApprovedStatus(status: string): boolean {
  const statusLower = status?.toLowerCase() || '';
  return (
    statusLower === 'approved' ||
    statusLower === 'approved_async' ||
    statusLower === 'approved_follow_up'
  );
}

/** Primary "Complete" statuses that map primary date → completedOn. */
function isSimpleCompleteStatus(status: string): boolean {
  const statusLower = status?.toLowerCase() || '';
  return statusLower === 'complete' || statusLower === 'complete_original_date';
}

function isDateEmptyOrInvalid(dateValue: any): boolean {
  if (!dateValue) return true;
  if (typeof dateValue === 'string') {
    const dateStr = dateValue.trim().toUpperCase();
    return dateStr === '' || dateStr === 'N/A' || dateStr === 'TBD';
  }
  if (
    typeof dateValue === 'object' &&
    dateValue !== null &&
    (dateValue as any).type === 'special'
  ) {
    const v = ((dateValue as any).value || '').toString().toUpperCase();
    return v === 'N/A' || v === 'TBD' || v === '';
  }
  return false;
}

function isMvpMilestoneName(name?: string): boolean {
  return name?.trim().toUpperCase() === 'MVP';
}

/**
 * Whether document_link is mandatory purely because Send for Approval is (or would be)
 * reachable — independent of the status-driven required-fields promotion in
 * milestoneConfigEnhanced (which only kicks in once status is already approved/complete).
 * MVP is exempt: it always sends a synthesized placeholder link (see save logic), so the
 * field stays hidden and never mandatory for it.
 */
function isDocumentLinkRequiredForSend(
  reviewType: string,
  status: string,
  sendForApproval: boolean,
  approvalRequired: boolean | undefined,
  milestoneName?: string
): boolean {
  if (isMvpMilestoneName(milestoneName)) return false;
  const policy = getMilestoneDocLinkPolicy(milestoneName);
  if (!policy || policy === 'NEVER') return false;
  return shouldShowSendForApprovalForm(
    reviewType,
    status,
    sendForApproval,
    approvalRequired
  );
}

/** TBD or N/A only (not empty) — for UC4 when user explicitly picks special after autofill. */
function isTbdOrNaValue(dateValue: any): boolean {
  if (!dateValue) return false;
  if (typeof dateValue === 'string') {
    const t = dateValue.trim().toUpperCase();
    return t === 'TBD' || t === 'N/A';
  }
  if (
    typeof dateValue === 'object' &&
    dateValue !== null &&
    (dateValue as any).type === 'special'
  ) {
    const t = ((dateValue as any).value || '').toString().toUpperCase();
    return t === 'TBD' || t === 'N/A';
  }
  return false;
}

interface MilestoneAttributes {
  async: boolean;
  audited: boolean;
  approvalRequired: boolean;
}

interface EditMilestoneModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Persists the milestone. Resolves to `false` when the modal should stay open
   * (save failed, or the Save-and-Send email didn't go out) and `true` when it's
   * safe to close.
   */
  onSave: (data: any) => Promise<boolean> | void;
  onApprovalCycleStart?: () => void;
  tier?: string; // Optional tier information (L1, L2, L3) for conditional default approver
  /** Product group codes for the moment (e.g. ["CPG", "DPG"]). Used to fetch default approvers. */
  productGroups?: string[];
  /**
   * Moment-level context (name, type, dates) from the `generalInfo` one level
   * above the milestones section. Used to build the readonly Send-for-Approval
   * subject line. Optional — the subject falls back to placeholders when absent.
   */
  sendForApprovalContext?: SlcApprovalSubjectContext;
  milestone?: {
    name: string;
    reviewType?: string; // Primary field name
    typeOfReview?: string; // Legacy support for existing data
    status?: string;
    date?: string;
    meetingScheduledOn?: string;
    meetingScheduledAt?: string;
    displayScheduleDateTime?: string; // NEW: UTC/GMT timestamp combining date + time for accurate cross-timezone display
    meetingLink?: string;
    secondaryDate?: string;
    documentLink?: string;
    recordingLink?: string;
    notesLink?: string;
    approver?: string;
    approverUid?: string;
    originalApprovedOn?: string;
    reApprovedOn?: string;
    approvedBy?: string;
    reApprovedBy?: string;
    /** Who rejected it, when status is Not Approved (incl. async variant). */
    notApprovedBy?: string;
    milestoneAttributes?: MilestoneAttributes;
    /** When set by API, used to suppress autofill for Approved On (UC5/UC6). */
    approvedOn?: string;
    /** When set by API, used to suppress autofill for Completed On (UC5/UC6). */
    completedOn?: string;
    /** `false` means default date from API and no user edit yet. */
    isEdited?: boolean;
    /** Prepopulated, readonly subject for the Send-for-Approval email. */
    subject?: string;
    /** Prepopulated, editable note for the Send-for-Approval email. */
    noteToApprover?: string;
    /** Persisted Send-for-Approval flag (round-tripped from the API). */
    sendForApproval?: boolean;
    /** Persisted CC recipients as a comma-joined list of names. */
    cc?: string;
    /** Persisted CC recipients as a comma-joined list of uids. */
    ccUid?: string;
    /** Whether the Send-for-Approval email has actually gone out (round-tripped from the API). */
    emailSent?: boolean;
  };
}

/**
 * Named alias for the `milestone` prop's shape, used (instead of `typeof
 * milestone`) as the parameter type on `applyMilestoneToFormData`/
 * `applyMilestoneToMeetingTimes` below — those functions locally shadow
 * `milestone` with their own binding, and `typeof milestone` would otherwise
 * resolve to that local shadow instead of the outer prop, tripping TS2502
 * ("referenced directly or indirectly in its own type annotation").
 */
type EditMilestoneModalMilestoneProp = EditMilestoneModalProps['milestone'];

export const EditMilestoneModal: React.FC<EditMilestoneModalProps> = ({
  open,
  onClose,
  onSave,
  onApprovalCycleStart,
  tier,
  productGroups = [],
  sendForApprovalContext,
  milestone
}) => {
  const loggedInUser = useSelector(selectUserProfile);
  // Read at fetch-resolution time so a profile that loads mid-request still applies.
  const loggedInUserRef = useRef(loggedInUser);
  loggedInUserRef.current = loggedInUser;

  // Mapping for review type display labels
  const reviewTypeDisplayMap: Record<string, string> = {
    TBD: 'TBD',
    'Milestone Not Required': 'N/A',
    Meeting: 'Meeting',
    Async: 'Async'
  };

  // Helper function to get display label for review type
  const getReviewTypeDisplayLabel = (value: string): string => {
    return reviewTypeDisplayMap[value] || value;
  };

  // Helper function to get review type value from display label
  const getReviewTypeValueFromDisplay = (displayLabel: string): string => {
    const entry = Object.entries(reviewTypeDisplayMap).find(
      ([_, display]) => display === displayLabel
    );
    return entry ? entry[0] : displayLabel;
  };

  // Helper function to ensure we always store backend values in formData
  const normalizeReviewTypeValue = (value: string): string => {
    // If the value is a display label (like "N/A"), convert it to backend value
    const backendValue = getReviewTypeValueFromDisplay(value);
    // If conversion found a match, use it; otherwise check if it's already a backend value
    if (backendValue !== value) {
      return backendValue;
    }
    // If it's already a backend value, return as is
    return value;
  };
  const [formData, setFormData] = useState({
    reviewType: normalizeReviewTypeValue(milestone?.reviewType || ''),
    status: milestone?.status || '',
    date: milestone?.date || '',
    meetingScheduledOn: milestone?.meetingScheduledOn || '',
    meetingScheduledAt: milestone?.meetingScheduledAt || '',
    meetingLink: milestone?.meetingLink || '',
    secondaryDate: milestone?.secondaryDate || '',
    documentLink: milestone?.documentLink || '',
    recordingLink: milestone?.recordingLink || '',
    notesLink: milestone?.notesLink || '',
    approver: parseCcContacts(milestone?.approver, milestone?.approverUid),
    originalApprovedOn: milestone?.originalApprovedOn || '',
    reApprovedOn: milestone?.reApprovedOn || '',
    approvedBy: milestone?.approvedBy || '',
    reApprovedBy: milestone?.reApprovedBy || '',
    notApprovedBy: milestone?.notApprovedBy || '',
    displayScheduleDateTime: milestone?.displayScheduleDateTime || '', // Add displayScheduleDateTime field
    // Send-for-Approval fields (Async + approval-sending statuses, e.g. Sent).
    // Prefill from persisted API data so the flow/form reappear on re-open.
    sendForApproval: milestone?.sendForApproval ?? false,
    cc: parseCcContacts(milestone?.cc, milestone?.ccUid) as any[],
    subject: milestone?.subject || '',
    noteToApprover: milestone?.noteToApprover || '',
    // Tracked in formData (not read from `milestone` directly at use-site) so a
    // reopen-for-feedback can reset it to `false` — otherwise the stale prop value
    // from the just-concluded approval round keeps computeApprovalFlowStage stuck
    // at "Pending" through the next Sent cycle. Resynced from the fresh prop like
    // every other field once an actual send updates `milestone.emailSent`.
    emailSent: milestone?.emailSent ?? false
  });

  const persistedApprovers = parseCcContacts(
    milestone?.approver,
    milestone?.approverUid
  );
  const hasApproverChanges =
    formData.approver.length !== persistedApprovers.length ||
    formData.approver.some(
      (approver, index) =>
        approver.uid !== persistedApprovers[index]?.uid ||
        approver.name !== persistedApprovers[index]?.name
    );

  /** Tracks Approved/Completed autofill state vs explicit user edits. */
  const approvedCompletedSessionRef = useRef({
    primaryDateFromAutofill: false,
    primaryDateTouchedByUser: false
  });

  /**
   * The view-only lock (`isApprovalReadOnly`) only applies `pointer-events: none`
   * via CSS, which blocks clicks but not keyboard Tab navigation or typing — a
   * user could still tab into a field and edit it. `inert` blocks focus,
   * keyboard input, and clicks for the whole subtree, closing that gap.
   */
  const formSectionRef = useRef<HTMLDivElement>(null);
  // Moving Approver into/out of the links row remounts its picker. Autofocus
  // belongs to initial form entry, not those later layout changes.
  const hasFormFocusRef = useRef(false);

  // Removed Spectrum TimeField state; using 24h string state from native input

  const [errors, setErrors] = useState<Record<string, string>>({});
  /**
   * Fields the user has already blurred at least once — standard "validate on
   * blur, then live" pattern (only shown here so validation doesn't fire while
   * they're still mid-typing a URL, but updates immediately on every keystroke
   * after that first blur). Lets errors surface as soon as the user moves on
   * from a field instead of only at Save, which was confusing when a field
   * like Document Link was filled in but failing URL validation silently —
   * "Send for Approval" stayed disabled with no visible reason why. Keyed by
   * the same formData property names computeValidationErrors() already uses.
   */
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  // True while awaiting the PATCH triggered by Save/Save and Send. The modal stays
  // open until the call settles — closing it optimistically would hide a
  // Save-and-Send failure (e.g. the email-not-sent case) from the user.
  const [isSaving, setIsSaving] = useState(false);

  // Calendar dialog states - removed since we're now using DateComponent

  // State for time with AM/PM combined (e.g., "9:00 AM")
  // Note: Initial parsing happens in useEffect to properly handle UTC conversion
  const [meetingStartTime, setMeetingStartTime] = useState<string>('');
  const [meetingEndTime, setMeetingEndTime] = useState<string>('');

  // Helper function to convert 12-hour time with AM/PM to 24-hour format
  const convert12to24 = (timeWithAMPM: string): string => {
    if (!timeWithAMPM) return '';
    const match = timeWithAMPM.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return '';

    const hourStr = match[1];
    const minute = match[2];
    const ampm = match[3].toUpperCase();
    let hour = parseInt(hourStr);

    if (ampm === 'PM' && hour !== 12) {
      hour += 12;
    } else if (ampm === 'AM' && hour === 12) {
      hour = 0;
    }

    return `${String(hour).padStart(2, '0')}:${minute}`;
  };

  // Timezone - always use PST (America/Los_Angeles)
  // Times are stored and displayed in user's local timezone
  const selectedTimezone = 'America/Los_Angeles'; // Always PST
  const [showStartTimeDropdown, setShowStartTimeDropdown] = useState(false);
  const [showEndTimeDropdown, setShowEndTimeDropdown] = useState(false);
  const [isFetchingApprovers, setIsFetchingApprovers] = useState(false);
  /** Fixed SLC-team Cc recipients (from GET default-approvers — one entry per configured
   *  address/group), shown as locked chips in the Send for Approval CC field so the user can
   *  preview them before ever submitting. undefined = not yet fetched this session. */
  const [slcTeamCc, setSlcTeamCc] = useState<SlcTeamContact[] | undefined>(
    undefined
  );

  // Generate time options in 30-minute increments (12-hour format with AM/PM included)
  // Working hours: 7:00 AM - 10:00 PM only
  const generateTimeOptions = (): Array<{ value: string; label: string }> => {
    const options: Array<{ value: string; label: string }> = [];

    // Generate AM times (7:00 AM to 11:30 AM)
    for (let hour = 7; hour <= 11; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time12 = `${hour}:${String(minute).padStart(2, '0')}`;
        const label = `${hour}:${String(minute).padStart(2, '0')} AM`;
        options.push({ value: time12 + ' AM', label });
      }
    }

    // Generate PM times (12:00 PM to 10:00 PM)
    // 12:00 PM and 12:30 PM
    for (let minute = 0; minute < 60; minute += 30) {
      const time12 = `12:${String(minute).padStart(2, '0')}`;
      const label = `12:${String(minute).padStart(2, '0')} PM`;
      options.push({ value: time12 + ' PM', label });
    }
    // 1:00 PM to 10:00 PM
    for (let hour = 1; hour <= 10; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time12 = `${hour}:${String(minute).padStart(2, '0')}`;
        const label = `${hour}:${String(minute).padStart(2, '0')} PM`;
        options.push({ value: time12 + ' PM', label });
      }
    }

    return options;
  };

  const timeOptions = generateTimeOptions();

  // Auto-scroll to 7:00 AM when start time dropdown opens
  useEffect(() => {
    if (showStartTimeDropdown) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const dropdown = document.querySelector(
          '.figma-timezone-dropdown-options'
        );
        if (dropdown) {
          // Scroll to top (7:00 AM is the first option)
          dropdown.scrollTop = 0;
        }
      }, 0);
    }
  }, [showStartTimeDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Close status/type dropdowns if click is outside any dropdown container
      if (!target.closest('.figma-type-dropdown-container')) {
        setShowStatusDropdown(false);
        setShowTypeDropdown(false);
      }

      // Close time dropdowns if click is outside time selector containers
      if (
        !target.closest('.figma-timezone-selector') &&
        !target.closest('.figma-timezone-dropdown-options')
      ) {
        setShowStartTimeDropdown(false);
        setShowEndTimeDropdown(false);
      }
    };

    // Always add the listener when modal is open, regardless of dropdown state
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []); // Remove dependencies to make it always active

  // Helper function to convert ISO date back to display format for DateComponent
  const convertISODateToDisplayFormat = (dateValue: any): any => {
    if (!dateValue) return '';

    // If it's already an object (quarter/month format), return as is
    if (typeof dateValue === 'object') {
      return dateValue;
    }

    // If it's a string, check if it's ISO date format
    if (typeof dateValue === 'string') {
      // Check if it's in YYYY-MM-DD format (ISO date)
      const isoMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoMatch) {
        // IMPORTANT FIX: Do NOT auto-convert day=1 to month format
        // The calendar already returns type:'months' for month selections
        // and type:'date' for specific date selections
        // We should trust the calendar's type designation and return the ISO date as-is
        // This fixes the bug where selecting Jan 1, 2025 was being displayed as "Jan 2025"
        return dateValue;
      }

      // Try to parse as JSON string
      try {
        const parsed = JSON.parse(dateValue);
        return parsed;
      } catch (e) {
        // Return as is if not JSON
        return dateValue;
      }
    }

    return dateValue;
  };

  // Clear errors (and which fields have been blurred) when modal opens or closes
  useEffect(() => {
    setErrors({});
    setTouchedFields(new Set());
    if (!open) hasFormFocusRef.current = false;
  }, [open]);

  // Builds the full formData snapshot for a given milestone (server-confirmed
  // shape) and applies it, resetting the approved/completed autofill session
  // tracking to match. Extracted so `handleUndoReopenToSent` can restore
  // exactly this same snapshot after `handleReopenToSent` clears fields
  // locally, without duplicating the field-by-field mapping.
  const applyMilestoneToFormData = (
    targetMilestone: EditMilestoneModalMilestoneProp
  ) => {
    if (targetMilestone) {
      approvedCompletedSessionRef.current.primaryDateFromAutofill = false;
      approvedCompletedSessionRef.current.primaryDateTouchedByUser = false;

      const milestone = targetMilestone;
      const newFormData = {
        reviewType: normalizeReviewTypeValue(milestone.reviewType || ''),
        status: milestone.status || '',
        date: convertISODateToDisplayFormat(milestone.date) || '',
        meetingScheduledOn:
          convertISODateToDisplayFormat(milestone.meetingScheduledOn) || '',
        meetingScheduledAt: milestone.meetingScheduledAt || '',
        meetingLink: normalizeURL(milestone.meetingLink || ''),
        secondaryDate: milestone.secondaryDate || '',
        documentLink: normalizeURL(milestone.documentLink || ''),
        recordingLink: normalizeURL(milestone.recordingLink || ''),
        notesLink: normalizeURL(milestone.notesLink || ''),
        approver: parseCcContacts(milestone.approver, milestone.approverUid),
        originalApprovedOn: milestone.originalApprovedOn || '',
        reApprovedOn: milestone.reApprovedOn || '',
        approvedBy: milestone.approvedBy || '',
        reApprovedBy: milestone.reApprovedBy || '',
        notApprovedBy: milestone.notApprovedBy || '',
        displayScheduleDateTime: milestone.displayScheduleDateTime || '', // Include existing displayScheduleDateTime
        // Send-for-Approval: prefill the toggle/CC/subject/note from persisted API
        // data so the flow + read-only form reappear on re-open.
        sendForApproval: milestone.sendForApproval ?? false,
        cc: parseCcContacts(milestone.cc, milestone.ccUid) as any[],
        subject: milestone.subject || '',
        noteToApprover: milestone.noteToApprover || '',
        emailSent: milestone.emailSent ?? false
      };

      // If backend has explicit approved/completed date fields, use them as fallback for primary date on open.
      // This avoids silently replacing persisted dates with "today" when generic `date` is empty.
      if (isDateEmptyOrInvalid(newFormData.date)) {
        if (
          isSimpleApprovedStatus(newFormData.status) &&
          milestone.approvedOn &&
          String(milestone.approvedOn).trim()
        ) {
          newFormData.date = String(milestone.approvedOn).trim();
        } else if (
          isSimpleCompleteStatus(newFormData.status) &&
          milestone.completedOn &&
          String(milestone.completedOn).trim()
        ) {
          newFormData.date = String(milestone.completedOn).trim();
        }
      }

      // UC1/UC2: modal opened with Approved/Completed but empty primary date — autofill today
      const todayIso = getYyyyMmDdInTimeZone();
      if (
        isSimpleApprovedStatus(newFormData.status) &&
        isDateEmptyOrInvalid(newFormData.date)
      ) {
        newFormData.date = todayIso;
        approvedCompletedSessionRef.current.primaryDateFromAutofill = true;
        approvedCompletedSessionRef.current.primaryDateTouchedByUser = false;
      } else if (
        isSimpleCompleteStatus(newFormData.status) &&
        isDateEmptyOrInvalid(newFormData.date)
      ) {
        newFormData.date = todayIso;
        approvedCompletedSessionRef.current.primaryDateFromAutofill = true;
        approvedCompletedSessionRef.current.primaryDateTouchedByUser = false;
      }

      setFormData(newFormData);
    }
  };

  // `milestone` is rebuilt as a brand-new object literal on every render of the
  // parent (MilestoneMatrixFromAPI constructs it inline in JSX) — its reference
  // changes even when nothing about the underlying data did. Keying this effect
  // on that reference alone re-seeds formData from the persisted milestone on
  // ANY unrelated parent re-render while the modal is open (e.g. PCO's
  // sendForApprovalContext recomputing per-phase), silently clobbering
  // in-progress unsaved edits — most visibly the "Type of Review" change meant
  // to lift `canReopenStatusField`'s restriction (see that function's doc).
  // Comparing a content snapshot instead of the reference lets a genuine data
  // change (e.g. a save round-trip landing a fresh `milestone` prop) still
  // re-seed, while a spurious same-content re-render is a no-op.
  const lastSyncedFormMilestoneSnapshotRef = useRef<string>('');
  useEffect(() => {
    const snapshot = JSON.stringify(milestone ?? null);
    if (snapshot === lastSyncedFormMilestoneSnapshotRef.current) {
      return;
    }
    lastSyncedFormMilestoneSnapshotRef.current = snapshot;
    applyMilestoneToFormData(milestone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestone]);

  // Pre-populate default approvers on open when backend init left approver empty (e.g. L3 uses l2-uids).
  useEffect(() => {
    if (!open || !milestone?.name || !isApproverEmpty(milestone.approver)) {
      return;
    }
    const statusForLookup = milestone.status || formData.status || undefined;
    setIsFetchingApprovers(true);
    fetchDefaultApprovers(milestone.name, productGroups, statusForLookup, tier)
      .then(result => {
        if (result?.slcTeamCc) {
          setSlcTeamCc(result.slcTeamCc);
        }
        const defaultApprovers = stripRequestorFromDefaults(
          result?.approvers,
          loggedInUserRef.current
        );
        if (defaultApprovers.length > 0) {
          const approverValues = defaultApprovers.map(a => ({
            uid: a.uid,
            name: a.name ?? a.uid
          }));
          setFormData(prev =>
            isApproverEmpty(prev.approver)
              ? { ...prev, approver: approverValues }
              : prev
          );
        }
      })
      .finally(() => setIsFetchingApprovers(false));
  }, [
    open,
    milestone?.name,
    milestone?.approver,
    milestone?.status,
    tier,
    productGroups
  ]);

  // Unconditionally resolves slcTeamCc once per milestone open — independent of the
  // approver-empty gate above, so the Send for Approval TO helper text can always show
  // the real address, even when the approver is already set (the common case) and the
  // effect above never fires. Skipped once already resolved so it isn't re-fetched on
  // every keystroke/status-change re-render.
  useEffect(() => {
    if (!open || !milestone?.name || slcTeamCc) {
      return;
    }
    const statusForLookup = milestone.status || formData.status || undefined;
    fetchDefaultApprovers(
      milestone.name,
      productGroups,
      statusForLookup,
      tier
    ).then(result => {
      if (result?.slcTeamCc) {
        setSlcTeamCc(result.slcTeamCc);
      }
    });
  }, [open, milestone?.name, slcTeamCc]);

  // Parses a milestone's meetingScheduledAt into the meetingStartTime/
  // meetingEndTime local state. Extracted (same reasoning as
  // `applyMilestoneToFormData` above) so `handleUndoReopenToSent` can restore
  // these too — they're local state set directly by `handleReopenToSent`
  // (cleared to '') and by this logic, not part of `formData`, so undoing the
  // reopen has to reapply this separately.
  const applyMilestoneToMeetingTimes = (
    targetMilestone: EditMilestoneModalMilestoneProp
  ) => {
    if (!targetMilestone) return;
    const milestone = targetMilestone;

    // Parse meetingScheduledAt for both start and end (range)
    const raw = (milestone && milestone.meetingScheduledAt) || '';

    // Try to parse with new timezone-aware utilities first
    const parsedTime = parseStoredMilestoneTime(
      milestone.meetingScheduledAt,
      milestone.meetingScheduledOn
    );

    if (parsedTime) {
      // Convert 24-hour format to 12-hour format with AM/PM
      if (parsedTime.startTime) {
        const [hour24, minute] = parsedTime.startTime.split(':').map(Number);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        setMeetingStartTime(
          `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`
        );
      }

      if (parsedTime.endTime) {
        const [hour24, minute] = parsedTime.endTime.split(':').map(Number);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        setMeetingEndTime(
          `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`
        );
      }

      return; // Exit early if timezone-aware parsing succeeded
    }

    // Check if the stored time is in UTC format
    const isUtcFormat = raw.includes('UTC');

    if (isUtcFormat && milestone.meetingScheduledOn) {
      // Convert UTC times back to selected timezone
      const startMatch = raw.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      const endMatch = raw.match(/-\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i);

      if (startMatch) {
        const utcStartTime = `${startMatch[1]}:${startMatch[2]} ${startMatch[3]}`;

        // Convert UTC to local time in selected timezone (24-hour format)
        const local24Start = utcLabelTimeToLocal24(
          milestone.meetingScheduledOn,
          utcStartTime,
          selectedTimezone
        );

        if (local24Start) {
          // Convert 24-hour format to 12-hour format with AM/PM
          const [hour24, minute] = local24Start.split(':').map(Number);
          const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
          const ampm = hour24 >= 12 ? 'PM' : 'AM';
          const formatted = `${hour12}:${String(minute).padStart(
            2,
            '0'
          )} ${ampm}`;
          setMeetingStartTime(formatted);
        }
      }

      if (endMatch) {
        const utcEndTime = `${endMatch[1]}:${endMatch[2]} ${endMatch[3]}`;

        // Convert UTC to local time in selected timezone (24-hour format)
        const local24End = utcLabelTimeToLocal24(
          milestone.meetingScheduledOn,
          utcEndTime,
          selectedTimezone
        );

        if (local24End) {
          // Convert 24-hour format to 12-hour format with AM/PM
          const [hour24, minute] = local24End.split(':').map(Number);
          const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
          const ampm = hour24 >= 12 ? 'PM' : 'AM';
          const formatted = `${hour12}:${String(minute).padStart(
            2,
            '0'
          )} ${ampm}`;
          setMeetingEndTime(formatted);
        }
      }
    } else {
      // For non-UTC format (legacy data), just parse as-is
      const startMatch = raw.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      const endMatch = raw.match(/-\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i);

      if (startMatch) {
        const hour = startMatch[1];
        const minute = startMatch[2];
        const ampm = startMatch[3].toUpperCase();
        setMeetingStartTime(`${hour}:${minute} ${ampm}`);
      }

      if (endMatch) {
        const hour = endMatch[1];
        const minute = endMatch[2];
        const ampm = endMatch[3].toUpperCase();
        setMeetingEndTime(`${hour}:${minute} ${ampm}`);
      }
    }
  };

  // When opening the modal or changing milestone, parse the time values.
  // Same reference-vs-content issue as the formData re-seed effect above —
  // `milestone`'s identity changes on every parent re-render regardless of
  // content, so compare a snapshot instead of relying on the dependency array
  // alone to decide whether to actually re-apply.
  const lastSyncedMeetingMilestoneSnapshotRef = useRef<string>('');
  const lastSyncedMeetingTimezoneRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const snapshot = JSON.stringify(milestone ?? null);
    const milestoneUnchanged =
      snapshot === lastSyncedMeetingMilestoneSnapshotRef.current;
    const timezoneUnchanged =
      selectedTimezone === lastSyncedMeetingTimezoneRef.current;
    lastSyncedMeetingMilestoneSnapshotRef.current = snapshot;
    lastSyncedMeetingTimezoneRef.current = selectedTimezone;
    if (milestoneUnchanged && timezoneUnchanged) {
      return;
    }
    applyMilestoneToMeetingTimes(milestone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestone, selectedTimezone]);

  // useEffect to ensure calendar/time overlays appear above modal/portals
  useEffect(() => {
    const ensureCalendarZIndex = () => {
      // Target all overlay containers that might contain calendar components
      const overlaySelectors = [
        '[data-overlay-container]',
        '[role="presentation"][style*="position: fixed"]',
        '.react-spectrum-Popover',
        '.spectrum-Popover',
        '[class*="spectrum-Popover"]',
        '[data-react-aria-top-layer="true"]'
      ];

      overlaySelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const htmlElement = element as HTMLElement;
          // Check if this overlay contains calendar-related content
          if (
            htmlElement.querySelector('[data-testid*="calendar"]') ||
            htmlElement.querySelector('[data-testid*="date-picker"]') ||
            htmlElement.querySelector('[data-testid*="time-picker"]') ||
            htmlElement.querySelector(
              '[data-testid*="time-picker-menu-trigger"]'
            ) ||
            htmlElement.querySelector('.calendar-range') ||
            htmlElement.textContent?.includes('Calendar')
          ) {
            htmlElement.style.zIndex = '2147483647';
          }
        });
      });
    };

    // Run immediately
    ensureCalendarZIndex();

    // Set up a MutationObserver to catch dynamically created overlays
    const observer = new MutationObserver(mutations => {
      let shouldCheck = false;
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Check if the added node or its children contain calendar-related elements
              if (
                element.matches?.('[data-overlay-container]') ||
                element.matches?.('[role="presentation"]') ||
                element.matches?.('.react-spectrum-Popover') ||
                element.querySelector?.('[data-testid*="calendar"]') ||
                element.querySelector?.('[data-testid*="date-picker"]')
              ) {
                shouldCheck = true;
              }
            }
          });
        }
      });

      if (shouldCheck) {
        // Small delay to ensure DOM is updated
        setTimeout(ensureCalendarZIndex, 10);
      }
    });

    // Observe the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also observe when date picker opens/closes
    const datePickerButtons = document.querySelectorAll(
      '[data-testid*="date-picker-menu-trigger"]'
    );
    datePickerButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(ensureCalendarZIndex, 100);
      });
    });

    // Cleanup
    return () => {
      observer.disconnect();
      datePickerButtons.forEach(button => {
        button.removeEventListener('click', ensureCalendarZIndex);
      });
    };
  }, []);

  // Recalculate displayScheduleDateTime whenever date or time changes
  // This ensures the cell display and hover popup show the correct date in real-time
  useEffect(() => {
    // Only recalculate for meeting_scheduled status
    if (formData.status !== 'meeting_scheduled') {
      // Clear displayScheduleDateTime if not meeting_scheduled
      if (formData.displayScheduleDateTime) {
        setFormData(prev => ({
          ...prev,
          displayScheduleDateTime: ''
        }));
      }
      return;
    }

    // Get the date to use - for meeting_scheduled, use the 'date' field
    // (meetingScheduledOn contains the UTC timestamp with time, not just the date)
    const dateValue = formData.date;

    // Convert date object to ISO string if needed
    let dateISO = '';
    if (dateValue && typeof dateValue === 'object') {
      // It's a date object from DateComponent, convert to ISO
      dateISO = convertDateObjectToISO(dateValue);
    } else if (typeof dateValue === 'string') {
      // If it's already an ISO timestamp (YYYY-MM-DDTHH:mm:ssZ), extract just the date part
      if (dateValue.match(/^\d{4}-\d{2}-\d{2}T/)) {
        dateISO = dateValue.substring(0, 10); // Extract YYYY-MM-DD
      } else {
        dateISO = dateValue;
      }
    }

    // Convert start time to 24-hour format
    const meetingStartTime24 = meetingStartTime
      ? convert12to24(meetingStartTime)
      : '';

    // Only calculate if we have both a valid date and time
    if (
      dateISO &&
      dateISO.match(/^\d{4}-\d{2}-\d{2}$/) && // Valid date format
      dateISO !== 'TBD' &&
      dateISO !== 'N/A' &&
      meetingStartTime24
    ) {
      const [hh, mm] = meetingStartTime24.split(':');
      const hour = Number(hh);
      const minute = Number(mm);

      if (!isNaN(hour) && !isNaN(minute)) {
        // Convert to UTC timestamp using the timezone utility
        const timezoneData = storeWithTimezone(
          dateISO,
          meetingStartTime24,
          selectedTimezone
        );

        if (timezoneData?.utcTimestamp) {
          // Only update if the value actually changed
          if (formData.displayScheduleDateTime !== timezoneData.utcTimestamp) {
            setFormData(prev => ({
              ...prev,
              displayScheduleDateTime: timezoneData.utcTimestamp
            }));
          }
        }
      }
    } else {
      // Clear displayScheduleDateTime if date or time is invalid
      if (formData.displayScheduleDateTime) {
        setFormData(prev => ({
          ...prev,
          displayScheduleDateTime: ''
        }));
      }
    }
  }, [formData.status, formData.date, meetingStartTime, selectedTimezone]);

  // Helper function to get field labels from configuration
  const getFieldLabel = (
    fieldId: string,
    status: string,
    reviewType: string
  ): string => {
    const statusKey = status?.toUpperCase();
    const enhancedStatusConfig = MILESTONE_STATUSES[statusKey];

    // Handle date field labels based on status and review type
    if (fieldId === 'date') {
      // First check combination-specific overrides from getExcelHoverData
      // Determine approval condition based on status configuration
      const approvalCondition = enhancedStatusConfig?.showApprover
        ? 'APPROVAL_REQUIRED'
        : 'NO_APPROVAL_REQUIRED';

      try {
        const hoverData = getExcelHoverData(
          approvalCondition,
          reviewType,
          status
        );
        if (hoverData?.rolloverCopy) {
          return hoverData.rolloverCopy.toUpperCase();
        }
      } catch (error) {
        // If getExcelHoverData fails, continue to fallback logic
      }

      // Second check enhanced configuration for rolloverCopy
      if (enhancedStatusConfig?.rolloverCopy) {
        return enhancedStatusConfig.rolloverCopy.toUpperCase();
      }

      // Fallback to legacy logic for backward compatibility
      if (statusKey?.includes('APPROVED')) {
        const baseLabel =
          enhancedStatusConfig?.historicalFields?.dateLabel || 'APPROVED ON';
        return baseLabel;
      } else if (statusKey === 'MEETING_SCHEDULED') {
        return 'MEETING SCHEDULED ON';
      } else if (statusKey === 'SENT') {
        return 'SENT ON';
      } else if (statusKey === 'TARGET') {
        // Special case: Meeting + Target = "MEETING TARGET"
        if (reviewType?.toLowerCase().includes('meeting')) {
          return 'MEETING TARGET';
        } else {
          return 'TARGET DATE';
        }
      } else {
        return 'DATE';
      }
    }

    // Handle meeting scheduled fields - get labels from configuration
    if (fieldId === 'meeting_scheduled_on') {
      const baseLabel =
        MILESTONE_FORM_FIELDS.MEETING_SCHEDULED_ON?.label ||
        'MEETING SCHEDULED ON';
      return baseLabel.toUpperCase();
    }

    if (fieldId === 'meeting_scheduled_at') {
      const baseLabel =
        MILESTONE_FORM_FIELDS.MEETING_SCHEDULED_AT?.label ||
        'MEETING SCHEDULED AT';
      return baseLabel.toUpperCase();
    }

    // Default fallback
    return fieldId.toUpperCase();
  };

  // Auto Save if TBD or NA is selected for review type
  const [isReviewTypeChanged, setIsReviewTypeChanged] = useState(false);
  React.useEffect(() => {
    if (
      isReviewTypeChanged &&
      (formData.reviewType === MILESTONE_REVIEW_TYPE.TBD ||
        formData.reviewType === MILESTONE_REVIEW_TYPE.MILESTONE_NOT_REQUIRED)
    ) {
      setIsReviewTypeChanged(false);
      handleSave();
    }
  }, [formData.reviewType, isReviewTypeChanged]);

  /** Default status when picking Meeting / Async (matches ENHANCED_STATUS_MENUS first option, lowercased like the status dropdown). */
  const defaultStatusForReviewType = useCallback(
    (reviewType: string): string => {
      if (reviewType === 'Meeting') return 'target';
      if (reviewType === 'Async') return 'async_target';
      return '';
    },
    []
  );

  /**
   * Snapshot of `formData.status`/`formData.secondaryDate` taken the moment
   * the user first navigates "Type of Review" away from the milestone's
   * persisted reviewType. Nothing has been saved during this detour, so if
   * they come back to the persisted reviewType before saving, this
   * in-progress state (e.g. a reopen-to-Sent that's still unresolved, plus
   * whatever Due Date was already entered for it) is restored exactly as it
   * was — rather than falling through to `defaultStatusForReviewType`'s
   * generic status default and an unconditionally blanked `secondaryDate`,
   * neither of which has any memory of the detour. `secondaryDate` is the
   * Async Send-for-Approval "Due Date" (see `sendForApprovalContext` below) —
   * exactly as valuable to preserve as `status` itself. Cleared once
   * consumed: a further detour starts a fresh snapshot of whatever is live
   * at that point, not this original one.
   */
  const reviewTypeDetourSnapshotRef = useRef<{
    status: string;
    secondaryDate: string;
  } | null>(null);

  const handleInputChange = (field: string, value: any) => {
    // If changing type of review, reset dependent fields
    if (field === 'reviewType') {
      setShowStatusDropdown(false); // Close dropdown when type changes
      setShowTypeDropdown(false); // Close type dropdown when changed

      const persistedReviewTypeNormalized = normalizeReviewTypeValue(
        milestone?.reviewType || ''
      );
      const isLeavingPersistedReviewType =
        formData.reviewType === persistedReviewTypeNormalized &&
        value !== persistedReviewTypeNormalized;
      const isReturningToPersistedReviewType =
        value === persistedReviewTypeNormalized;

      if (isLeavingPersistedReviewType) {
        reviewTypeDetourSnapshotRef.current = {
          status: formData.status,
          secondaryDate: formData.secondaryDate || ''
        };
      }
      const detourSnapshot = reviewTypeDetourSnapshotRef.current;
      const canRestoreDetourSnapshot =
        isReturningToPersistedReviewType && detourSnapshot !== null;
      const nextStatus = canRestoreDetourSnapshot
        ? detourSnapshot.status
        : defaultStatusForReviewType(value);
      const nextSecondaryDate = canRestoreDetourSnapshot
        ? detourSnapshot.secondaryDate
        : '';
      if (isReturningToPersistedReviewType) {
        reviewTypeDetourSnapshotRef.current = null;
      }

      const currentDate = formData.date || '';
      let nextDate: any;

      // Same session rules as status change: autofill-only clears; user edit (any) is kept.
      if (approvedCompletedSessionRef.current.primaryDateTouchedByUser) {
        nextDate = currentDate;
      } else if (approvedCompletedSessionRef.current.primaryDateFromAutofill) {
        nextDate = '';
        approvedCompletedSessionRef.current.primaryDateFromAutofill = false;
      } else {
        if (!currentDate) {
          nextDate = '';
        } else if (typeof currentDate === 'string') {
          const dateStr = currentDate.trim().toUpperCase();
          nextDate =
            dateStr !== '' && dateStr !== 'N/A' && dateStr !== 'TBD'
              ? currentDate
              : '';
        } else {
          nextDate = currentDate;
        }
      }

      const resetsApprovalCycle =
        isInactiveApprovalReviewType(value) ||
        (value === MILESTONE_REVIEW_TYPE.ASYNC &&
          isInactiveApprovalReviewType(formData.reviewType));
      if (resetsApprovalCycle) onApprovalCycleStart?.();

      setFormData(prev => ({
        ...prev,
        ...(resetsApprovalCycle ? getNewApprovalCycleState([]) : {}),
        [field]: value,
        status: nextStatus,
        date: nextDate,
        meetingScheduledOn: '',
        meetingScheduledAt: '',
        meetingLink: '',
        secondaryDate: nextSecondaryDate
        // Preserve approver - don't reset when review type changes
        // Don't reset link fields - let user keep them if they want
        // documentLink: '',
        // recordingLink: '',
        // notesLink: '',
      }));
      setIsReviewTypeChanged(true);
    } else if (field === 'status') {
      // When status changes, preserve existing date unless it's N/A or TBD
      // Example: Not Approved (date: 2024-01-15) → Not Approved Follow-up should keep 2024-01-15
      // If changing FROM a status that allowed N/A (like TARGET) TO a status that requires a date (like SCHEDULED),
      // clear the N/A so user can enter a new date - N/A should NEVER carry forward
      const prevStatus = formData.status || '';
      const newStatus = value as string;
      const currentDate = formData.date || '';

      // Check if current date is invalid or N/A/TBD - handle both string and object formats
      let isCurrentDateInvalid = false;

      if (!currentDate) {
        // Empty or null date
        isCurrentDateInvalid = true;
      } else if (typeof currentDate === 'string') {
        // String format: check if empty, N/A, or TBD
        const dateStr = currentDate.trim().toUpperCase();
        isCurrentDateInvalid =
          dateStr === '' || dateStr === 'N/A' || dateStr === 'TBD';
      } else if (
        typeof currentDate === 'object' &&
        (currentDate as any).type === 'special'
      ) {
        // Object format from DateComponent: {type: "special", value: "N/A"} or {type: "special", value: "TBD"}
        const dateValue = ((currentDate as any).value || '')
          .toString()
          .toUpperCase();
        isCurrentDateInvalid = dateValue === 'N/A' || dateValue === 'TBD';
      }

      // If user set TBD/N/A after a prior edit, keep it on status change (UC4) — do not coalesce to '' here.
      let nextDate: any;
      if (
        approvedCompletedSessionRef.current.primaryDateTouchedByUser &&
        isTbdOrNaValue(currentDate)
      ) {
        nextDate = currentDate;
      } else {
        nextDate = isCurrentDateInvalid ? '' : currentDate;
      }

      const prevIsApproved = isSimpleApprovedStatus(prevStatus);
      const prevIsComplete = isSimpleCompleteStatus(prevStatus);
      const newIsApproved = isSimpleApprovedStatus(newStatus);
      const newIsComplete = isSimpleCompleteStatus(newStatus);

      // UC3/UC4: leaving Approved or Completed for an unrelated status — clear session autofill only
      const leavingApprovedOrComplete =
        (prevIsApproved || prevIsComplete) && !newIsApproved && !newIsComplete;

      if (leavingApprovedOrComplete) {
        if (approvedCompletedSessionRef.current.primaryDateFromAutofill) {
          nextDate = '';
        }
        approvedCompletedSessionRef.current.primaryDateFromAutofill = false;
      }

      // Include hops between approved variants (e.g. approved ↔ approved_follow_up) so "today" applies like re-select.
      const enteringApproved =
        newIsApproved &&
        (!prevIsApproved ||
          prevStatus.toLowerCase() !== String(newStatus).toLowerCase());
      const enteringComplete =
        newIsComplete &&
        (!prevIsComplete ||
          prevStatus.toLowerCase() !== String(newStatus).toLowerCase());
      const todayIso = getYyyyMmDdInTimeZone();

      // Keep user-chosen TBD/N/A from another status (e.g. Target) — do not replace with today on entry.
      const userChoseTbdOrNaToKeep =
        approvedCompletedSessionRef.current.primaryDateTouchedByUser &&
        isTbdOrNaValue(nextDate);

      const shouldAutofillOnEntry =
        !userChoseTbdOrNaToKeep &&
        (isDateEmptyOrInvalid(nextDate) ||
          !approvedCompletedSessionRef.current.primaryDateTouchedByUser);

      if ((enteringApproved || enteringComplete) && shouldAutofillOnEntry) {
        nextDate = todayIso;
        approvedCompletedSessionRef.current.primaryDateFromAutofill = true;
        approvedCompletedSessionRef.current.primaryDateTouchedByUser = false;
      }

      // Async + Sent: default "Sent On" to today and "Due Date" to today + 2 business
      // days (weekends skipped, e.g. Fri → next Tue).
      let nextSecondaryDate = '';
      // True whenever this change genuinely (re-)enters Sent from something else —
      // e.g. Not Approved manually moved back to Sent for another round. Excludes
      // staying on Sent (re-picking the same value) so in-progress Send-for-Approval
      // edits on an already-Sent milestone are never wiped out from under the user.
      // The Approved/Re-Approved → Sent reopen path is handled separately by
      // `handleReopenToSent`, not through this generic branch.
      const isReenteringAsyncSent =
        formData.reviewType === 'Async' &&
        String(newStatus).toLowerCase() === 'sent' &&
        prevStatus.toLowerCase() !== 'sent';
      if (isReenteringAsyncSent) onApprovalCycleStart?.();
      if (
        formData.reviewType === 'Async' &&
        String(newStatus).toLowerCase() === 'sent'
      ) {
        nextDate = todayIso;
        nextSecondaryDate = addBusinessDaysIso(todayIso, 2);
        approvedCompletedSessionRef.current.primaryDateFromAutofill = false;
        approvedCompletedSessionRef.current.primaryDateTouchedByUser = false;
      }

      // Manually picking Approved/Re-Approved (Async) supersedes any earlier
      // Send-for-Approval state — clear the flag rather than leave a stale
      // Sent/Pending indicator around a milestone that's now approved.
      const clearSendForApprovalOnApprove =
        shouldClearSendForApprovalOnManualStatusChange({
          status: newStatus,
          reviewType: formData.reviewType
        });

      setFormData(prev => ({
        ...prev,
        ...(isReenteringAsyncSent ? getNewApprovalCycleState([]) : {}),
        [field]: value,
        date: nextDate,
        // Reset meeting-specific fields when status changes
        meetingScheduledOn: '', // Clear meeting scheduled date
        meetingScheduledAt: '', // Clear meeting time
        meetingLink: '', // Clear meeting link
        secondaryDate: nextSecondaryDate, // Async+Sent: due date autofill; otherwise cleared
        // Preserve approver - don't reset when status changes
        sendForApproval:
          clearSendForApprovalOnApprove || isReenteringAsyncSent
            ? false
            : prev.sendForApproval
      }));

      // Auto-populate default approvers when moving to a status that requires an approver
      // and the approver field is currently empty. Only pre-fills — user can always override.
      if (
        statusRequiresApproverField(newStatus) &&
        isApproverEmpty(formData.approver) &&
        milestone?.name
      ) {
        setIsFetchingApprovers(true);
        fetchDefaultApprovers(milestone.name, productGroups, newStatus, tier)
          .then(result => {
            if (result?.slcTeamCc) {
              setSlcTeamCc(result.slcTeamCc);
            }
            const defaultApprovers = stripRequestorFromDefaults(
              result?.approvers,
              loggedInUserRef.current
            );
            if (defaultApprovers.length > 0) {
              const approverValues = defaultApprovers.map(a => ({
                uid: a.uid,
                name: a.name ?? a.uid
              }));
              setFormData(prev =>
                isApproverEmpty(prev.approver)
                  ? { ...prev, approver: approverValues }
                  : prev
              );
            }
          })
          .finally(() => setIsFetchingApprovers(false));
      }

      // Also clear the time fields in state
      setMeetingStartTime('');
      setMeetingEndTime('');
    } else {
      if (field === 'date') {
        // UC4/UC7: any user change (including clear) ends session autofill tracking
        approvedCompletedSessionRef.current.primaryDateFromAutofill = false;
        approvedCompletedSessionRef.current.primaryDateTouchedByUser = true;
      }
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Real-time date relationship validation
    if ((field === 'date' || field === 'secondaryDate') && formData.status) {
      validateDateRelationshipsRealTime(field, value);
    }
  };

  /**
   * Reopen for feedback: picking "Sent" from the status dropdown while an
   * Async milestone is Approved/Re-Approved (the one non-disabled option
   * besides the current status itself — see the status dropdown's
   * `isReopenOptionEnabled` check) moves it back to Sent for another round of
   * async approval. Clears the prior approval outcome (approver/date fields,
   * `sendForApproval`) and reapplies the same autofill used the first time a
   * milestone is manually set to Sent — today's date + due date = today + 2
   * business days (mirrors the "Async + Sent" branch of `handleInputChange`
   * above).
   *
   * Clearing `sendForApproval` also drops `approvalFlowStage` back to `null`
   * on the next render, which lifts the read-only lock (`isApprovalReadOnly`)
   * the same way it would for a freshly-created Sent milestone — no separate
   * unlock logic needed here.
   */
  const handleReopenToSent = () => {
    onApprovalCycleStart?.();
    const todayIso = getYyyyMmDdInTimeZone();
    approvedCompletedSessionRef.current.primaryDateFromAutofill = false;
    approvedCompletedSessionRef.current.primaryDateTouchedByUser = false;
    setFormData(prev => ({
      ...prev,
      status: 'sent',
      date: todayIso,
      secondaryDate: addBusinessDaysIso(todayIso, 2),
      meetingScheduledOn: '',
      meetingScheduledAt: '',
      meetingLink: '',
      ...getNewApprovalCycleState([])
    }));
    setMeetingStartTime('');
    setMeetingEndTime('');
    // A fresh Sent cycle is a clean slate for validation too — same reasoning
    // as the mount effect at `[open]` above. Without this, an error set by
    // `validateDateRelationshipsRealTime` (which writes directly to `errors`,
    // not gated on `touchedFields`) or by a field the user blurred moments
    // before reopening can linger on screen pointing at a field this reset
    // just cleared/changed, even though it's valid again.
    setErrors({});
    setTouchedFields(new Set());
  };

  /**
   * Undo `handleReopenToSent`: picking the ORIGINAL concluded status back
   * (the one exact status `milestone.status` still holds server-side, since
   * a local reopen-to-Sent that hasn't been saved never touched the server)
   * restores every field `handleReopenToSent` cleared, exactly as it was
   * before "Sent" was clicked — a true cancel, not a fresh manual
   * approve/reject. Reuses the same milestone->formData mapping the modal
   * applies on mount (`applyMilestoneToFormData`/`applyMilestoneToMeetingTimes`)
   * rather than re-deriving a partial subset of fields, so nothing
   * `handleReopenToSent` touches (approver, dates, meeting info, Cc/notes)
   * is left stale.
   */
  const handleUndoReopenToSent = () => {
    applyMilestoneToFormData(milestone);
    applyMilestoneToMeetingTimes(milestone);
    // Full revert to server-confirmed data — any validation error accumulated
    // while reopened (see handleReopenToSent's own reasoning above) is
    // necessarily stale now too.
    setErrors({});
    setTouchedFields(new Set());
  };

  // URL field focus handlers removed - no longer auto-filling prefix

  // Real-time date relationship validation function
  const validateDateRelationshipsRealTime = (
    changedField: string,
    changedValue: any
  ) => {
    const statusConfig = MILESTONE_STATUSES[formData.status.toUpperCase()];
    if (!statusConfig?.dateRelationshipRules) return;

    const fieldMapping: Record<string, keyof typeof formData> = {
      secondary_date: 'secondaryDate',
      date: 'date',
      original_approved_on: 'originalApprovedOn',
      re_approved_on: 'reApprovedOn'
    };

    // Create updated form data with the new value
    const updatedFormData = {
      ...formData,
      [changedField]: changedValue
    };

    Object.entries(statusConfig.dateRelationshipRules).forEach(
      ([field, rule]) => {
        const formFieldName = fieldMapping[field] || field;
        const compareFormFieldName =
          fieldMapping[rule.compareToField] || rule.compareToField;

        // Check if the changed field affects this validation rule
        if (
          changedField === formFieldName ||
          changedField === compareFormFieldName
        ) {
          const fieldValue =
            updatedFormData[formFieldName as keyof typeof formData];
          const compareFieldValue =
            updatedFormData[compareFormFieldName as keyof typeof formData];

          if (fieldValue && compareFieldValue) {
            const dateValidationResult = validateDateRelationship(
              fieldValue,
              compareFieldValue,
              rule.relationshipType,
              rule.allowSameDate
            );

            if (!dateValidationResult.isValid) {
              setErrors(prev => ({
                ...prev,
                [formFieldName]: rule.errorMessage
              }));
            } else {
              // Clear the error if validation passes
              setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[formFieldName];
                return newErrors;
              });
            }
          }
        }
      }
    );
  };

  // ─── Shared constants for link field management ───
  // Single definition of all link field IDs and their form key mapping.
  // Used by validateForm, isFieldRequired, URL validation, and error display.
  const ALL_LINK_FIELD_IDS = new Set([
    'document_link',
    'recording_link',
    'notes_link',
    'meeting_link'
  ]);

  const LINK_FIELD_TO_FORM_KEY: Record<string, keyof typeof formData> = {
    document_link: 'documentLink',
    recording_link: 'recordingLink',
    notes_link: 'notesLink',
    meeting_link: 'meetingLink'
  };

  // Pure computation of the milestone form's validation errors — no state writes.
  // Shared by validateForm (on save) and the Send-for-Approval enable gate so both
  // stay in sync without duplicating the required-field rules.
  const computeValidationErrors = (
    requireApproverUid = hasApproverChanges ||
      isAsyncSentSendForApprovalAttempt({
        status: formData.status,
        reviewType: formData.reviewType,
        sendForApproval: formData.sendForApproval
      })
  ): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    const excluded = getMilestoneExcludedReviewTypes(milestone?.name);
    if (excluded.includes(formData.reviewType)) {
      newErrors['reviewType'] =
        'Type of Review is not supported for this milestone type';
    }

    const enhancedFields = getEnhancedVisibleFields(
      formData.reviewType,
      formData.status,
      undefined,
      tier,
      getMilestoneApprovalRequired(milestone?.name) ??
        milestone?.milestoneAttributes?.approvalRequired,
      milestone?.name
    );
    const requiredFields = enhancedFields?.required || [];

    // Get the set of link fields actually rendered in the form
    // Only these link fields should be validated - all others are skipped
    // Note: getRenderedLinkFields() is called once here because validateForm() is synchronous
    // and form state (formData, milestone) doesn't change during validation execution
    const renderedLinkFields = getRenderedLinkFields();

    requiredFields.forEach(fieldId => {
      // For link fields: skip validation if the field is NOT rendered in the form
      // Uses getRenderedLinkFields() as the single source of truth
      if (ALL_LINK_FIELD_IDS.has(fieldId) && !renderedLinkFields.has(fieldId)) {
        return;
      }

      const fieldConfig = MILESTONE_FORM_FIELDS[fieldId.toUpperCase()];

      // Map field IDs to formData property names
      const fieldMapping: Record<string, keyof typeof formData> = {
        type_of_review: 'reviewType',
        status: 'status',
        date: 'date',
        meeting_scheduled_on: 'meetingScheduledOn',
        meeting_scheduled_at: 'meetingScheduledAt',
        meeting_link: 'meetingLink',
        secondary_date: 'secondaryDate',
        document_link: 'documentLink',
        recording_link: 'recordingLink',
        notes_link: 'notesLink',
        approver: 'approver',
        original_approved_on: 'originalApprovedOn',
        re_approved_on: 'reApprovedOn',
        approved_by: 'approvedBy',
        re_approved_by: 'reApprovedBy'
      };

      const formFieldName = fieldMapping[fieldId] || fieldId;
      const fieldValue = formData[formFieldName as keyof typeof formData];

      // Special handling for approver field which can be LDAP object, array, or string
      if (fieldId === 'approver') {
        const isApproverEmpty =
          !fieldValue ||
          (typeof fieldValue === 'string' && fieldValue.trim() === '') ||
          (Array.isArray(fieldValue) && fieldValue.length === 0) ||
          (typeof fieldValue === 'object' &&
            !Array.isArray(fieldValue) &&
            (!(fieldValue as any).name ||
              (fieldValue as any).name.trim() === ''));

        if (isApproverEmpty) {
          newErrors[formFieldName] = `${
            fieldConfig?.label || 'Approver'
          } is required`;
        }
      } else if (!fieldValue || fieldValue.toString().trim() === '') {
        newErrors[formFieldName] = `${
          fieldConfig?.label || fieldId
        } is required`;
      }
    });

    // Send for Approval always needs at least one approver, independent of the tier-based
    // required-fields rule above — this is what makes approver mandatory for L3/other-tier
    // milestones (where it's otherwise optional). Checked as soon as the Send-for-Approval
    // section is reachable (not only once the checkbox is ticked) so the checkbox itself
    // stays disabled until an approver is picked, same as the other mandatory fields above.
    if (
      !newErrors['approver'] &&
      shouldShowSendForApprovalForm(
        formData.reviewType,
        formData.status,
        formData.sendForApproval,
        resolvedApprovalRequired
      ) &&
      isApproverEmpty(formData.approver)
    ) {
      newErrors['approver'] = 'Approver is required to send for approval';
    }

    if (
      !isApprovalReadOnly &&
      getApproverContacts().some(approver => isSameUser(approver, loggedInUser))
    ) {
      newErrors['approver'] = 'You cannot select yourself as an approver.';
    }

    if (
      requireApproverUid &&
      !newErrors['approver'] &&
      getApproverContacts().some(approver => !approver.uid?.trim())
    ) {
      newErrors['approver'] =
        'Approver UID is missing. Remove and reselect the approver.';
    }

    // A milestone whose doc-link policy requires a link on approval/completion (see backend
    // MilestoneDocLinkValidator) must already have one attached before Send for Approval — an
    // approver replying by email has no way to attach a document themselves.
    if (
      !newErrors['documentLink'] &&
      isDocumentLinkRequiredForSend(
        formData.reviewType,
        formData.status,
        formData.sendForApproval,
        resolvedApprovalRequired,
        milestone?.name
      ) &&
      (!formData.documentLink || formData.documentLink.toString().trim() === '')
    ) {
      newErrors['documentLink'] =
        'A document link is required to send for approval — the approver cannot attach one via email reply';
    }

    // Custom validation for meeting scheduled status - require both start and end times
    if (formData.status === 'meeting_scheduled') {
      if (!meetingStartTime || meetingStartTime.trim() === '') {
        newErrors['meetingStartTime'] =
          'Start time is required for scheduled meetings';
      }
      if (!meetingEndTime || meetingEndTime.trim() === '') {
        newErrors['meetingEndTime'] =
          'End time is required for scheduled meetings';
      }
      // Validate that end time is after start time
      if (meetingStartTime && meetingEndTime) {
        const startTime24 = convert12to24(meetingStartTime);
        const endTime24 = convert12to24(meetingEndTime);

        if (startTime24 && endTime24) {
          const [startHour, startMin] = startTime24.split(':').map(Number);
          const [endHour, endMin] = endTime24.split(':').map(Number);
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;

          if (endMinutes <= startMinutes) {
            newErrors['meetingEndTime'] = 'End time must be after start time';
          }
        }
      }
    }

    // URL validation for link fields - only validate fields that are actually rendered in the form
    // Uses getRenderedLinkFields() as the single source of truth
    renderedLinkFields.forEach(fieldId => {
      const formFieldName = LINK_FIELD_TO_FORM_KEY[fieldId];
      if (!formFieldName) return;

      // Safely handle null, undefined, or empty values
      const rawValue = formData[formFieldName];
      if (!rawValue || typeof rawValue !== 'string' || !rawValue.trim()) {
        return; // Skip validation for empty/null/undefined values
      }

      // Normalize protocol to lowercase before validation so HTTPS:// is treated as https://
      const value = normalizeURL(rawValue);

      // document_link: allowlist; other link fields: generic URL only
      const linkError = validateMilestoneLinkField(fieldId, value);
      if (linkError) {
        newErrors[formFieldName] = linkError;
      }
    });

    // Date relationship validation using enhanced milestone configuration
    if (formData.status) {
      const statusConfig = MILESTONE_STATUSES[formData.status.toUpperCase()];
      if (statusConfig?.dateRelationshipRules) {
        Object.entries(statusConfig.dateRelationshipRules).forEach(
          ([field, rule]) => {
            const fieldMapping: Record<string, keyof typeof formData> = {
              secondary_date: 'secondaryDate',
              date: 'date',
              original_approved_on: 'originalApprovedOn',
              re_approved_on: 'reApprovedOn'
            };

            const formFieldName = fieldMapping[field] || field;
            const compareFormFieldName =
              fieldMapping[rule.compareToField] || rule.compareToField;

            const fieldValue = formData[formFieldName as keyof typeof formData];
            const compareFieldValue =
              formData[compareFormFieldName as keyof typeof formData];

            if (fieldValue && compareFieldValue) {
              const dateValidationResult = validateDateRelationship(
                fieldValue,
                compareFieldValue,
                rule.relationshipType,
                rule.allowSameDate
              );

              if (!dateValidationResult.isValid) {
                newErrors[formFieldName] = rule.errorMessage;
              }
            }
          }
        );
      }
    }

    return newErrors;
  };

  const validateForm = (): {
    isValid: boolean;
    errors: Record<string, string>;
  } => {
    const newErrors = computeValidationErrors();
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return { isValid, errors: newErrors };
  };

  /** Marks a field as blurred at least once — see touchedFields above. */
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields(prev =>
      prev.has(fieldName) ? prev : new Set(prev).add(fieldName)
    );
  };

  // Keep touched fields' errors live (re-validated on every change) instead of
  // only at Save — see touchedFields above for why. Deliberately scoped to
  // only the fields the user has already blurred: computeValidationErrors()
  // covers the whole form, but showing all of it immediately on any single
  // blur would flag fields the user hasn't even reached yet.
  useEffect(() => {
    if (touchedFields.size === 0) return;
    const currentErrors = computeValidationErrors();
    setErrors(prev => {
      const next = { ...prev };
      touchedFields.forEach(fieldName => {
        next[fieldName] = currentErrors[fieldName] || '';
      });
      return next;
    });
    // computeValidationErrors reads formData/meetingStartTime/meetingEndTime
    // directly (not memoized), so it's re-run on every change to any of them —
    // matching how isPreviousFormValid below already stays live.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchedFields, formData, meetingStartTime, meetingEndTime]);

  // Helper function to determine if a field is required based on enhanced configuration
  // For link fields, uses getRenderedLinkFields() as the single source of truth to check
  // if the field is actually rendered in the form. If a link field is not rendered, it's never required.
  const isFieldRequired = (fieldId: string): boolean => {
    // For link fields, check if the field is rendered in the form first
    // Uses getRenderedLinkFields() to determine visibility
    if (
      ALL_LINK_FIELD_IDS.has(fieldId) &&
      !getRenderedLinkFields().has(fieldId)
    ) {
      return false; // Not rendered = not required
    }

    const enhancedFields = getEnhancedVisibleFields(
      formData.reviewType,
      formData.status,
      undefined,
      tier,
      getMilestoneApprovalRequired(milestone?.name) ??
        milestone?.milestoneAttributes?.approvalRequired,
      milestone?.name
    );
    if (enhancedFields?.required?.includes(fieldId)) {
      return true;
    }

    // document_link: also required (asterisk shown) as soon as Send for Approval is
    // reachable and this milestone's doc-link policy applies — not just once status is
    // already approved/complete. Keeps the visual cue in sync with computeValidationErrors.
    if (fieldId === 'document_link') {
      return isDocumentLinkRequiredForSend(
        formData.reviewType,
        formData.status,
        formData.sendForApproval,
        resolvedApprovalRequired,
        milestone?.name
      );
    }

    // approver: also required (asterisk shown) as soon as Send for Approval is
    // reachable — mirrors the identical rule in computeValidationErrors (see
    // "Send for Approval always needs at least one approver..." there) so the
    // visual cue never disagrees with the actual validation error. The tier's
    // own required-fields list can leave approver optional here (e.g. L3/other
    // tiers), which is exactly the case that rule exists to cover.
    if (fieldId === 'approver') {
      return shouldShowSendForApprovalForm(
        formData.reviewType,
        formData.status,
        formData.sendForApproval,
        resolvedApprovalRequired
      );
    }

    return false;
  };

  // Helper function to determine if link fields should be shown
  const shouldShowLinkFields = (): boolean => {
    // Don't show link fields for TBD and Not Needed scenarios
    if (['TBD', 'Milestone Not Required'].includes(formData.reviewType)) {
      return false;
    }

    // For Meeting/Async types, only show link fields AFTER a status is selected
    if (['Meeting', 'Async'].includes(formData.reviewType)) {
      return (
        typeof formData.status === 'string' && formData.status.trim() !== ''
      );
    }

    return false;
  };

  // ─── SINGLE SOURCE OF TRUTH for which link fields are rendered in the form ───
  // Returns the set of link field IDs that are actually visible/rendered in the UI.
  // Both validation (required + URL) and error display MUST use this function.
  // If a link field is not in this set, it must NEVER be validated or show errors.
  const getRenderedLinkFields = (): Set<string> => {
    // If link fields section is not shown at all, no link fields are rendered
    if (!shouldShowLinkFields()) {
      return new Set();
    }

    const rendered = new Set<string>();

    // notes_link: rendered when in configVisibleFields from enhanced config
    const enhancedFieldsConfig = getEnhancedVisibleFields(
      formData.reviewType,
      formData.status,
      undefined,
      tier,
      getMilestoneApprovalRequired(milestone?.name) ??
        milestone?.milestoneAttributes?.approvalRequired,
      milestone?.name
    );

    // document_link: rendered unless hidden by MILESTONE_NAME_OVERRIDES config
    if (!enhancedFieldsConfig?.hidden?.includes('document_link')) {
      rendered.add('document_link');
    }

    // configVisibleFields: fields that COULD be visible according to config
    // rendered: fields that ARE actually rendered in the UI (subset of configVisibleFields)
    const configVisibleFields = [
      ...(enhancedFieldsConfig?.required || []),
      ...(enhancedFieldsConfig?.optional || [])
    ];

    if (configVisibleFields.includes('notes_link')) {
      rendered.add('notes_link');
    }

    // recording_link: always rendered when link fields section is shown
    rendered.add('recording_link');

    // meeting_link: rendered if in config visible fields (for meeting_scheduled status)
    if (configVisibleFields.includes('meeting_link')) {
      rendered.add('meeting_link');
    }

    return rendered;
  };

  // Helper: check if link field errors should be displayed (consolidated error message)
  const getConsolidatedLinkFieldError = (): string | undefined => {
    const renderedLinks = getRenderedLinkFields();
    for (const fieldId of Array.from(renderedLinks)) {
      const formKey = LINK_FIELD_TO_FORM_KEY[fieldId];
      if (formKey && errors[formKey]) {
        return errors[formKey];
      }
    }
    return undefined;
  };

  // Helper function to determine if status-dependent fields should be shown
  const shouldShowStatusDependentFields = (): boolean => {
    // Only show status-dependent fields if we have both type and status selected
    return (
      ['Meeting', 'Async'].includes(formData.reviewType) &&
      typeof formData.status === 'string' &&
      formData.status.trim() !== ''
    );
  };

  // Calendar helper functions
  const toISO = (date: CalendarDate) =>
    `${date.year}-${String(date.month).padStart(2, '0')}-${String(
      date.day
    ).padStart(2, '0')}`;

  // Calendar helper functions removed since we're now using DateComponent
  // URL validation via validateMilestoneLinkField in validators.ts

  // Helper function to normalize URL by adding protocol if missing
  const normalizeURL = (url: string): string => {
    if (!url || !url.trim()) return url;
    let trimmedUrl = url.trim();
    // Heal double-prefix corruption: https://HTTPS://... → https://...
    trimmedUrl = trimmedUrl.replace(/^https?:\/\/(https?:\/\/)/i, (_, inner) =>
      inner.toLowerCase()
    );
    // If it already has a protocol, normalize it to lowercase
    if (trimmedUrl.match(/^https?:\/\//i)) {
      return trimmedUrl.replace(/^https?:\/\//i, match => match.toLowerCase());
    }
    // Add https:// if missing
    return `https://${trimmedUrl}`;
  };

  // Helper function to convert date objects while preserving months/quarters format
  const convertDateObjectToISO = (dateValue: any): string => {
    if (!dateValue) return '';

    // If it's already a string (ISO date), return as is
    if (typeof dateValue === 'string') {
      // Check if it's already in YYYY-MM-DD format
      if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateValue;
      }

      // Try to parse JSON string
      try {
        const parsed = JSON.parse(dateValue);
        return convertDateObjectToISO(parsed);
      } catch (e) {
        return dateValue;
      }
    }

    // Handle object format
    if (typeof dateValue === 'object') {
      // For special dates (TBD, N/A): preserve the original format
      if (dateValue.type === 'special' && dateValue.value) {
        // Return the special value as-is (e.g., "TBD", "N/A")
        return dateValue.value;
      }

      // For quarters: preserve the original format, don't convert to ISO
      if (
        dateValue.type === 'quarters' &&
        Array.isArray(dateValue.quarters) &&
        dateValue.quarters.length > 0
      ) {
        // Return the quarter string as-is (e.g., "Q1 2026")
        return dateValue.quarters[0];
      }

      // For months: preserve the original format, don't convert to ISO
      if (
        dateValue.type === 'months' &&
        Array.isArray(dateValue.months) &&
        dateValue.months.length > 0
      ) {
        // Return the month string as-is (e.g., "Nov 2025")
        return dateValue.months[0];
      }

      // Handle nested date objects - only convert actual dates to ISO
      if (dateValue.type === 'date' && dateValue.date) {
        return convertDateObjectToISO(dateValue.date);
      }
    }

    return '';
  };

  const handleSave = () => {
    const currentFields = getEnhancedVisibleFields(
      formData.reviewType,
      formData.status,
      undefined,
      tier,
      getMilestoneApprovalRequired(milestone?.name) ??
        milestone?.milestoneAttributes?.approvalRequired,
      milestone?.name
    );

    const validationResult = validateForm();
    const { isValid, errors: validationErrors } = validationResult;

    if (!isValid) {
      // Validation errors are shown below each field, no toast needed
      return; // Stop save if validation fails
    }

    if (isValid) {
      // Convert quarter/month objects to ISO dates
      const convertedMeetingScheduledOn = convertDateObjectToISO(
        formData.meetingScheduledOn
      );
      const convertedDate = convertDateObjectToISO(formData.date);
      const convertedSecondaryDate = convertDateObjectToISO(
        formData.secondaryDate
      );

      // ⭐ VALIDATION: Meeting Scheduled status requires specific dates, not month/quarter
      if (formData.status === 'meeting_scheduled') {
        // Validate meeting_scheduled_on field
        if (convertedMeetingScheduledOn) {
          const isMonthFormat =
            convertedMeetingScheduledOn.match(/^[A-Za-z]+\s+\d{4}$/);
          const isQuarterFormat =
            convertedMeetingScheduledOn.match(/^Q[1-4]\s+\d{4}$/);

          if (isMonthFormat || isQuarterFormat) {
            setErrors(prev => ({
              ...prev,
              meetingScheduledOn:
                'Meeting Scheduled On requires a specific date. Please select an exact date instead of a month or quarter.'
            }));
            return; // Stop save
          }
        }

        // Validate date field (if used instead of meeting_scheduled_on)
        if (convertedDate) {
          const isMonthFormat = convertedDate.match(/^[A-Za-z]+\s+\d{4}$/);
          const isQuarterFormat = convertedDate.match(/^Q[1-4]\s+\d{4}$/);

          if (isMonthFormat || isQuarterFormat) {
            setErrors(prev => ({
              ...prev,
              date: 'Meeting Scheduled date requires a specific date. Please select an exact date instead of a month or quarter.'
            }));
            return; // Stop save
          }
        }
      }

      let utcMeetingScheduledOn = convertedMeetingScheduledOn;
      let utcTimeRange = '';

      // For meeting scheduled status, prioritize meetingScheduledOn field
      // For other statuses, use date field for the main date
      const dateToUse =
        formData.status === 'meeting_scheduled'
          ? convertedMeetingScheduledOn || convertedDate
          : convertedDate || convertedMeetingScheduledOn;

      // Convert 12-hour time to 24-hour format for processing
      const meetingStartTime24 = convert12to24(meetingStartTime);
      const meetingEndTime24 = convert12to24(meetingEndTime);

      // IMPORTANT: Only capture time when status is "meeting_scheduled"
      // For all other statuses, do NOT capture or store time information
      if (formData.status === 'meeting_scheduled') {
        // Skip time validation and conversion for special dates (TBD, N/A)
        const isSpecialDate = dateToUse === 'TBD' || dateToUse === 'N/A';

        if (!isSpecialDate) {
          // Check if user has actually entered time
          const hasExistingTime =
            milestone?.meetingScheduledAt &&
            milestone.meetingScheduledAt.trim() !== '';
          const userEnteredTime =
            (meetingStartTime && meetingStartTime.trim() !== '') ||
            hasExistingTime;

          if (!userEnteredTime) {
            // User hasn't entered time - show validation error
            alert('Please enter meeting time for Meeting Scheduled status');
            return; // Stop save
          }
        }

        // Only convert to UTC with time if the date is a specific date (YYYY-MM-DD format)
        // For quarters (Q1 2025) or months (Nov 2025), also convert time to UTC
        // Skip time conversion for special dates (TBD, N/A)
        const isSpecificDate =
          dateToUse && dateToUse.match(/^\d{4}-\d{2}-\d{2}$/);
        const isSpecialDateForTime = dateToUse === 'TBD' || dateToUse === 'N/A';

        if (isSpecificDate && meetingStartTime24 && !isSpecialDateForTime) {
          const [hh, mm] = meetingStartTime24.split(':');
          const hour = Number(hh);
          const minute = Number(mm);

          // Only proceed if we have valid hour and minute values
          if (!isNaN(hour) && !isNaN(minute)) {
            // Store datetime with timezone information
            const timezoneData = storeWithTimezone(
              dateToUse,
              meetingStartTime24,
              selectedTimezone
            );

            if (timezoneData) {
              // Store UTC timestamp for meetingScheduledOn
              utcMeetingScheduledOn = timezoneData.utcTimestamp;

              // Store time range in user's local timezone for display
              utcTimeRange = formatLocalTimeRange(
                meetingStartTime24,
                meetingEndTime24,
                dateToUse,
                selectedTimezone
              );
            } else {
              // Fallback to old method
              const converted = toUtcIsoFromLocalParts(
                dateToUse,
                { hour, minute },
                { zone: selectedTimezone }
              );
              utcMeetingScheduledOn = converted || dateToUse;
              utcTimeRange = formatLocalTimeRange(
                meetingStartTime24,
                meetingEndTime24,
                dateToUse,
                selectedTimezone
              );
            }
          } else {
            // Invalid time values, just use the date without time
            utcMeetingScheduledOn = dateToUse;
          }
        } else if (dateToUse && !isSpecificDate && !isSpecialDateForTime) {
          // For quarter/month dates, store the date as-is but CONVERT time to UTC
          utcMeetingScheduledOn = dateToUse;

          // Use formatUtcTimeRange to properly convert time to UTC
          if (meetingStartTime24) {
            const [hh, mm] = meetingStartTime24.split(':');
            const hour = Number(hh);
            const minute = Number(mm);

            if (!isNaN(hour) && !isNaN(minute)) {
              // For quarter/month, we need a reference date for conversion
              // Use first day of current year for conversion reference
              const referenceDate = new Date().getFullYear() + '-01-01';

              utcTimeRange = formatLocalTimeRange(
                meetingStartTime24,
                meetingEndTime24,
                referenceDate,
                selectedTimezone
              );
            }
          }
        } else if (isSpecialDateForTime) {
          // For special dates (TBD, N/A), just store the date without time
          utcMeetingScheduledOn = dateToUse;
          utcTimeRange = ''; // No time for special dates
        }
      } else {
        // For non-meeting-scheduled statuses, just store the date without any time
        utcMeetingScheduledOn = dateToUse;
        utcTimeRange = ''; // Clear time range for non-meeting-scheduled statuses
      }

      // Helper function to check if a date value is TBD or N/A
      const isSpecialDateValue = (dateValue: any): boolean => {
        return dateValue === 'TBD' || dateValue === 'N/A';
      };

      // Determine if any critical dates are TBD or N/A
      const hasTBDOrNADates =
        isSpecialDateValue(convertedDate) ||
        isSpecialDateValue(convertedMeetingScheduledOn) ||
        isSpecialDateValue(convertedSecondaryDate);

      // Prefer the UTC timestamp computed in this save pass (utcMeetingScheduledOn is
      // already a full ISO string like "2026-06-26T16:00:00Z" when time was successfully
      // converted). Fall back to the reactive formData value for edge cases where the
      // save-time conversion didn't run (e.g. TBD/N/A dates, non-meeting statuses).
      const displayScheduleDateTime =
        utcMeetingScheduledOn && utcMeetingScheduledOn.includes('T')
          ? utcMeetingScheduledOn
          : formData.displayScheduleDateTime || '';

      // MVP still needs a document link in the milestone payload for backend
      // validation, even though the UI click behavior does not use it.
      const isMVP = isMvpMilestoneName(milestone?.name);
      const documentLinkForSave = isMVP
        ? 'https://mvp-status-link.com'
        : normalizeURL(formData.documentLink);

      // Prepare base form data with converted dates
      const baseFormData = {
        ...formData,
        date: convertedDate || formData.date,
        secondaryDate: convertedSecondaryDate || formData.secondaryDate,
        meetingScheduledOn: utcMeetingScheduledOn,
        meetingScheduledAt: utcTimeRange,
        approver: Array.isArray(formData.approver)
          ? formData.approver.map((user: any) => user.name).join(', ')
          : typeof formData.approver === 'object' &&
            (formData.approver as any)?.name
          ? (formData.approver as any).name
          : formData.approver,
        approverUid: Array.isArray(formData.approver)
          ? formData.approver
              .map((user: any) => user.uid)
              .filter(Boolean)
              .join(', ')
          : typeof formData.approver === 'object' &&
            (formData.approver as any)?.uid
          ? (formData.approver as any).uid
          : ''
      };

      // Map dates to correct backend field names based on status
      // This ensures dates go to the right fields (e.g., approvedOn, notApprovedOn, sentOn, etc.)
      const mappedDateFields = mapMilestoneDateFields(baseFormData);

      const saveData = {
        ...formData,
        // Ensure reviewType uses backend value, not display value
        reviewType: formData.reviewType, // This is already the backend value since we store backend values in formData
        // Use converted ISO dates instead of raw objects
        date: convertedDate || formData.date,
        meetingScheduledOn: utcMeetingScheduledOn, // Store UTC timestamp here (backward compatibility)
        meetingScheduledAt: utcTimeRange, // Store time range with user's timezone (backward compatibility)
        displayScheduleDateTime, // NEW: Single source of truth for date+time in UTC
        // Convert secondaryDate from object to string (TBD, N/A, or ISO date)
        secondaryDate: convertedSecondaryDate || formData.secondaryDate,
        // Apply mapped date fields (e.g., approvedOn, notApprovedOn, sentOn, etc.)
        ...mappedDateFields,
        // Explicitly store the user's timezone for accurate display later
        userTimezone: selectedTimezone,
        userTimezoneAbbr: getTimezoneAbbreviation(selectedTimezone),
        // Unrelated saves must preserve historical recipients, including missing UID slots.
        approver: hasApproverChanges
          ? formData.approver.map(user => user.name).join(', ')
          : milestone?.approver || '',
        approverUid: hasApproverChanges
          ? formData.approver.map(user => user.uid).join(', ')
          : milestone?.approverUid || '',
        // Clear approval-related fields when dates are TBD/N/A
        // This ensures the backend receives clean data for TBD/N/A scenarios
        approvedBy: hasTBDOrNADates ? '' : formData.approvedBy || '',
        originalApprovedOn: hasTBDOrNADates
          ? ''
          : formData.originalApprovedOn || '',
        reApprovedOn: hasTBDOrNADates ? '' : formData.reApprovedOn || '',
        reApprovedBy: hasTBDOrNADates ? '' : formData.reApprovedBy || '',
        // Normalize URL fields by adding https:// if missing.
        documentLink: documentLinkForSave,
        recordingLink: normalizeURL(formData.recordingLink),
        notesLink: normalizeURL(formData.notesLink),
        meetingLink: normalizeURL(formData.meetingLink)
      };

      // Keep timezone info - don't delete it
      // This ensures the exact timezone used during creation is preserved
      // delete (saveData as any).timeZone;
      // delete (saveData as any).timeZoneShort;

      // Send-for-Approval payload rules:
      // - subject is built client-side (buildSlcApprovalSubject) and sent
      //   ONLY when the box is checked, so the emailed subject matches what the
      //   readonly field shows.
      // - sendForApproval / cc / ccUid / noteToApprover are sent ONLY when the
      //   box is checked AND this save is the actual "send" action — Async
      //   review type at the `sent` status. Once status has moved past `sent`
      //   (Approved/Re-approved/etc.), the flag is just carried-over form state
      //   and re-sending these on every later save would re-trigger nothing
      //   useful, so they're stripped instead.
      // First strip the raw values leaked in via `...formData`, then add the flattened
      // ones (like `approver`) when opted in.
      delete (saveData as any).subject;
      delete (saveData as any).cc;
      delete (saveData as any).noteToApprover;
      // Server-computed (whether the approval-request email actually went
      // out) — never accepted on this PATCH by the backend's field schema, so
      // sending it just produces a silent skipped-field warning on every save
      // for every milestone. Read back via formData.emailSent from whatever
      // the milestone prop/PATCH response actually reports.
      delete (saveData as any).emailSent;

      const isAsyncSentSendForApproval = isAsyncSentSendForApprovalAttempt({
        status: formData.status,
        reviewType: formData.reviewType,
        sendForApproval: formData.sendForApproval
      });

      // Explicit `false` (never just omitted/deleted): MilestoneMatrixFromAPI's
      // save handler merges this payload over the milestone's LAST-LOADED data
      // (`{...cleanMilestoneData, ...updatedData}`), and `sendForApproval` on
      // that stale object is a server-computed "has this milestone EVER been
      // sent" flag — `true` for any milestone with an approval-request history,
      // regardless of its current status. Omitting the key here (e.g. via
      // `delete`) let that stale `true` silently win the merge, re-triggering
      // an actual send on a plain save (e.g. Not Approved → reopen to Sent →
      // change back to Not Approved → Save) even though nothing here asked
      // for one.
      (saveData as any).sendForApproval = false;

      if (isAsyncSentSendForApproval) {
        const ccContacts = Array.isArray(formData.cc) ? formData.cc : [];
        (saveData as any).sendForApproval = true;
        (saveData as any).subject = sendForApprovalSubject;
        (saveData as any).cc = ccContacts
          .map((user: any) => user.name)
          .filter(Boolean)
          .join(', ');
        (saveData as any).ccUid = ccContacts
          .map((user: any) => user.uid)
          .filter(Boolean)
          .join(', ');
        (saveData as any).noteToApprover = formData.noteToApprover;
      }

      setIsSaving(true);
      Promise.resolve(onSave(saveData))
        .then(shouldClose => {
          if (shouldClose !== false) {
            onClose();
          }
        })
        .finally(() => setIsSaving(false));
    } else {
    }
  };

  const handleCancelClick = () => {
    setIsReviewTypeChanged(false);
    onClose();
  };

  const getApproverContacts = (): ApprovalCcContact[] => formData.approver;

  // Resolved approvalRequired: config override (RR1/RR2/MKTG PKG/Media Brainstorm
  // are hardcoded false) takes precedence over the API-supplied value. Used to
  // gate the Send-for-Approval flow itself — see shouldShowSendForApprovalForm.
  const resolvedApprovalRequired =
    getMilestoneApprovalRequired(milestone?.name) ??
    milestone?.milestoneAttributes?.approvalRequired;

  const showSendForApproval = shouldShowSendForApprovalForm(
    formData.reviewType,
    formData.status,
    formData.sendForApproval,
    resolvedApprovalRequired
  );

  // Whether the Send-for-Approval body (Cc/Subject/Note — the tall part) is
  // actually expanded. Mirrors the `sendForApproval &&` gate SendForApprovalForm
  // itself uses to render that body — the one thing that reliably grows this
  // form past the viewport. Drives whether the modal constrains/scrolls at all
  // (see `.figma-modal-content.is-scrollable`) so a short form (e.g. a plain
  // Meeting-type date field) never gets an overflow boundary it doesn't need,
  // which would otherwise clip that field's own calendar popover.
  const isSendForApprovalExpanded =
    showSendForApproval && formData.sendForApproval;

  // The Send-for-Approval form + checkbox are only EDITABLE at the `sent` status.
  // For the downstream statuses (Sent - Feedback Pending / Approved / Re-Approved)
  // the form is shown read-only so the flag persists as the status changes.
  const isSentStatus = (formData.status || '').toLowerCase() === 'sent';

  // True once 'sent' is the persisted status (not just a live, unsaved
  // selection/reopen) — e.g. it was saved without ever triggering an email
  // (Send-for-Approval checkbox left unchecked, or approval not required at
  // all). In that case emailSent is falsy but the status genuinely IS Sent,
  // so the label should read "Sent", not "Send for Review".
  const isSentStatusPersisted =
    isSentStatus && (milestone?.status || '').toLowerCase() === 'sent';

  // Active stage of the Async approval flow (Add/Edit → Send for Approval →
  // Pending → Approved). Null when the flow should not be shown at all — i.e.
  // not Async, status outside the approval set, or not sent for approval.
  const approvalFlowStage = computeApprovalFlowStage({
    sendForApproval: formData.sendForApproval,
    reviewType: formData.reviewType,
    status: formData.status,
    emailSent: formData.emailSent
  });

  // View-only lock: the entire modal becomes read-only (no Save) once an Async
  // milestone with the sendForApproval flag set reaches Pending / Approved /
  // Reapproved. The `sent` stage stays editable. Not Approved has no stage of
  // its own in `approvalFlowStage` (see `isNotApprovedStatus`'s doc comment —
  // it's deliberately absent from the 4-step stepper so it never shows a
  // misleading "Approved" checkmark), but it's just as concluded a
  // Send-for-Approval outcome as Approved/Re-Approved, so it gets the same
  // lock here via a separate OR clause rather than folding it into the
  // stepper's own stage numbering.
  const isApprovalReadOnly =
    (approvalFlowStage !== null &&
      approvalFlowStage >= ApprovalFlowStage.PENDING) ||
    (formData.reviewType === MILESTONE_REVIEW_TYPE.ASYNC &&
      !!formData.sendForApproval &&
      isNotApprovedStatus(formData.status));

  // See `canReopenStatusField` in `utils/approvalFlow.ts` for the full
  // reasoning — in short, this only restricts the dropdown for a milestone
  // that actually went through a real Send-for-Approval round (Async +
  // persisted sendForApproval true), keyed off `milestone?.status`/
  // `milestone?.reviewType`/`milestone?.sendForApproval` (server-confirmed)
  // and `isSaving`, never `formData`'s live equivalents — so reopening to
  // Sent locally (unsaved) can never prematurely unlock the rest of the
  // status dropdown, and a Meeting-reviewType or manually-approved-without-
  // email Async milestone is never restricted at all. `formData.reviewType`
  // is passed as the one live exception: if the user live-switches Type of
  // Review away from Async in this same unsaved edit, the restriction must
  // lift immediately, since the dropdown's options switch to that review
  // type's own status vocabulary.
  const canReopenStatusField = resolveCanReopenStatusField(
    milestone?.status,
    milestone?.reviewType,
    milestone?.sendForApproval,
    isSaving,
    formData.reviewType
  );

  /**
   * Once an Async Send-for-Approval milestone reaches a concluded status
   * (Approved / Re-Approved / Not Approved), the top Approver field has
   * nothing left to "pick" — it's locked (isApprovalReadOnly) same as the
   * rest of the form — so showing the full pool of designated approvers
   * (formData.approver, which can be several people) is misleading: only one
   * of them actually acted. Switches the field to a single read-only name —
   * whoever is recorded in approvedBy/reApprovedBy/notApprovedBy for that
   * outcome — with a label naming the actual action taken, reusing the same
   * read-only-select presentation the legacy Meeting-type "Approver Details
   * Section" below already uses for the same purpose.
   */
  const approverResolution = (() => {
    if (!isApprovalReadOnly) return null;
    const statusLower = (formData.status || '')
      .toLowerCase()
      .replace(/_async$/, '');
    if (statusLower === 're_approved') {
      return { label: 'RE-APPROVED BY', name: formData.reApprovedBy || '' };
    }
    if (statusLower === 'approved') {
      return { label: 'APPROVED BY', name: formData.approvedBy || '' };
    }
    if (statusLower === 'not_approved') {
      return { label: 'REJECTED BY', name: formData.notApprovedBy || '' };
    }
    return null;
  })();

  /** Shared content for both Approver field render sites (merged-into-links-row
   * and dedicated-section) — see `approverResolution` above for when this
   * shows a read-only resolved name instead of the live LDAP picker. */
  const renderApproverFieldContent = () => {
    // Same field, same LDAP pill styling either way — only the label and which
    // contact(s) populate it change. Once resolved, it's already locked via
    // the section-wide `inert` (isApprovalReadOnly), so no separate read-only
    // treatment is needed here.
    const approverContacts = approverResolution
      ? approverResolution.name
        ? [{ uid: approverResolution.name, name: approverResolution.name }]
        : []
      : getApproverContacts();

    return (
      <>
        <label className="figma-field-label">
          {approverResolution ? approverResolution.label : 'APPROVER'}
          {!approverResolution && isFieldRequired('approver') && (
            <span> *</span>
          )}
          {!approverResolution && isFetchingApprovers && (
            <span
              style={{
                marginLeft: 6,
                fontWeight: 400,
                fontSize: 11,
                color: '#666'
              }}
            >
              Loading defaults…
            </span>
          )}
        </label>
        <div className="figma-ldap-wrapper">
          <LDAPSearchField
            label=""
            value={approverContacts}
            onChange={selectedUsers => {
              // Handle multiple user selection for approver field
              handleInputChange('approver', selectedUsers || []);
            }}
            isRequired={false}
            autoFocus={
              !isApprovalReadOnly &&
              !isSendForApprovalExpanded &&
              !hasFormFocusRef.current
            }
            selfSelection={{
              user: loggedInUser,
              message: 'You cannot select yourself as an approver.'
            }}
            cssClasses={{
              ldapFieldContainer: 'figma-ldap-field-container',
              pickerContainer: 'figma-ldap-picker-container',
              ldapTextField: 'figma-ldap-text-field',
              selectedTag: 'figma-ldap-selected-tag',
              tagRemoveButton: 'figma-ldap-tag-remove-button',
              ldapDropdown: 'figma-ldap-dropdown'
            }}
          />
        </div>
        {!approverResolution && errors.approver && (
          <div className="figma-error-message">{errors.approver}</div>
        )}
      </>
    );
  };

  // Keep the form section's `inert` attribute in sync with the read-only lock.
  // Set imperatively (rather than as a JSX prop) since `inert` isn't part of
  // @types/react's HTMLAttributes in this React 18 setup.
  //
  // `inert` cascades to descendants with no way to selectively opt a child
  // back in, so to let the STATUS field alone stay usable while Approved/
  // Re-Approved (`canReopenStatusField`), inert is applied per-field (via
  // `.figma-form-field`, each tagged with `data-field`) instead of once on the
  // whole container — every field is locked except the one tagged
  // `data-field="status"`. In every other locked state (e.g. Pending) the
  // whole section, status field included, stays fully inert as before.
  //
  // Also locks while `isSaving` — otherwise the fields stay fully interactive
  // for the entire PATCH round trip (only the Save button itself was
  // disabled), so a user could edit values and re-submit against a request
  // that's still in flight. `canReopenStatusField` itself also excludes
  // `isSaving` (see its own comment), so a save still inerts every field,
  // status included, even for a concluded milestone that isn't otherwise
  // `isApprovalReadOnly`.
  //
  // useLayoutEffect (not useEffect): useEffect fires *after* the browser
  // paints, so there's a brief window — on initial mount for an
  // already-locked milestone, or right after a Save-and-Send response flips
  // `emailSent`/`isApprovalReadOnly` true — where React has rendered the
  // unlocked JSX but `inert` hasn't been applied yet. A click landing in that
  // window (e.g. a fast double-click right after Save and Send resolves)
  // could still reach the status dropdown before the lock catches up.
  // useLayoutEffect runs synchronously after DOM mutations but before paint,
  // closing that gap entirely.
  const isFormLocked = isApprovalReadOnly || isSaving;
  useLayoutEffect(() => {
    const el = formSectionRef.current;
    if (!el) return;
    el.removeAttribute('inert');
    if (!isFormLocked) {
      // Symmetric with the lock branch below, which inerts every `.figma-form-field`
      // (not just ones tagged `data-field`) — e.g. SendForApprovalForm's own Cc/
      // Subject/Note fields. Clearing only `[data-field]` here left those
      // permanently inert after the first Approved lock, even once reopened back
      // to Sent.
      el.querySelectorAll('[data-field], .figma-form-field').forEach(node =>
        node.removeAttribute('inert')
      );
      return;
    }
    el.querySelectorAll('.figma-form-field').forEach(node => {
      if (
        canReopenStatusField &&
        node.getAttribute('data-field') === 'status'
      ) {
        node.removeAttribute('inert');
      } else {
        node.setAttribute('inert', '');
      }
    });
  }, [isFormLocked, canReopenStatusField]);

  // Readonly Send-for-Approval subject, built from the moment context (tier,
  // release-style date per moment type, moment name) plus the milestone being
  // edited and its Due Date (secondaryDate). Recomputes as the Due Date autofills
  // or the user edits it. Single source of truth for both display and payload.
  const sendForApprovalSubject = React.useMemo(
    () =>
      buildSlcApprovalSubject({
        ...sendForApprovalContext,
        tier,
        milestoneName: milestone?.name,
        dueDate: formData.secondaryDate
      }),
    [sendForApprovalContext, tier, milestone?.name, formData.secondaryDate]
  );

  // Keep the Send-for-Approval checkbox disabled until every mandatory field in the
  // milestone form above is filled/valid (dates, approver, links, etc.).
  const isPreviousFormValid =
    !showSendForApproval ||
    Object.keys(computeValidationErrors(true)).length === 0;

  // If the form becomes invalid after the user already checked "Send for Approval",
  // uncheck it too — so once they fix the errors they consciously opt in again.
  // Only applies while editing at the `sent` status; downstream statuses show the
  // form read-only and must not reset a flag the user already set.
  useEffect(() => {
    if (isSentStatus && formData.sendForApproval && !isPreviousFormValid) {
      setFormData(prev => ({ ...prev, sendForApproval: false }));
    }
  }, [isSentStatus, formData.sendForApproval, isPreviousFormValid]);

  const getPreviewText = (): string => {
    if (!formData.status) return '';

    // Keep preview purely from config; no inline time concatenation needed

    return formatMilestoneDisplayText(
      formData.status.toUpperCase(),
      formData.reviewType || ''
    );
  };

  if (!open) return null;

  // Debug: Log every render to track what's happening

  const enhancedFieldsConfig = getEnhancedVisibleFields(
    formData.reviewType,
    formData.status,
    undefined,
    tier,
    getMilestoneApprovalRequired(milestone?.name) ??
      milestone?.milestoneAttributes?.approvalRequired,
    milestone?.name
  );
  const visibleFields = [
    ...(enhancedFieldsConfig?.required || []),
    ...(enhancedFieldsConfig?.optional || [])
  ];
  const hiddenFields = enhancedFieldsConfig?.hidden || [];
  const isApproverVisible =
    shouldShowStatusDependentFields() &&
    visibleFields.includes('approver') &&
    !hiddenFields.includes('approver');
  // Whether Approver joins the links grid (Row 2, alongside Meeting Notes —
  // see the "Content Links Section" below) instead of the separate dedicated
  // section (separator + its own row). Deliberately has no dependency on
  // which specific link fields are visible/hidden — only on whether the
  // actual Send-for-Approval flow is active (Async review type + the
  // checkbox checked). Checking only formData.sendForApproval isn't enough
  // on its own: that flag can be true from persisted data even for a
  // Meeting-type milestone where the Send for Approval flow doesn't apply at
  // all, which would wrongly merge Approver into the links grid there too.
  const approverMergesIntoLinksRow =
    formData.reviewType === MILESTONE_REVIEW_TYPE.ASYNC &&
    !!formData.sendForApproval;
  const excludedReviewTypes = getMilestoneExcludedReviewTypes(milestone?.name);
  const statusOptions = getStatusOptions(
    formData.reviewType,
    getMilestoneApprovalRequired(milestone?.name) ??
      milestone?.milestoneAttributes?.approvalRequired
  );
  const cellStyle = getCellStyling(formData.status.toUpperCase());
  const statusConfig = MILESTONE_STATUSES[formData.status.toUpperCase()];

  // Helper function to get calendar restrictions for a specific field from the status config
  const getCalendarRestrictions = (fieldId: string) => {
    if (statusConfig?.calendarFieldRestrictions?.[fieldId]) {
      return statusConfig.calendarFieldRestrictions[fieldId];
    }
    return {};
  };

  // Debug: Log field calculations

  return ReactDOM.createPortal(
    <div
      className="figma-modal-overlay edit-milestone"
      style={{ zIndex: '2147483648' }}
    >
      <div
        className={`figma-modal-container${
          isSendForApprovalExpanded ? ' is-scrollable' : ''
        }`}
      >
        <div
          className={`figma-milestone-container${
            isSendForApprovalExpanded ? ' is-scrollable' : ''
          }`}
        >
          {/* Header */}
          <div className="figma-modal-header">
            <div className="figma-modal-header-content">
              <div className="figma-modal-title-section">
                <h1 className="figma-modal-title">Edit</h1>
              </div>
            </div>
          </div>

          {/*
            Static block (title + approval-required badge + stepper) — kept
            outside .figma-modal-content, alongside the header/footer, so it
            stays in place when the form below scrolls. See
            .figma-modal-content's overflow-y: auto for the actual scroll
            region.
          */}
          <div className="figma-milestone-title-section">
            <div className="figma-milestone-title-header">
              <div className="figma-milestone-name">{milestone?.name}</div>
              {(getMilestoneApprovalRequired(milestone?.name) ??
                milestone?.milestoneAttributes?.approvalRequired) && (
                <div className="figma-milestone-badge">
                  <span className="figma-badge-letter">A</span>
                  <span className="figma-badge-text">(Approval Required)</span>
                </div>
              )}
            </div>
          </div>

          {/* Approval flow — Async milestones that have been sent for approval */}
          {approvalFlowStage !== null && (
            <ApprovalFlowSteps activeStage={approvalFlowStage} />
          )}

          {/* Content — the scrollable region; header/title/stepper above and
              footer below stay fixed in place. Only actually constrained
              (capped height + scroll) once the Send-for-Approval body is
              expanded — that's the one thing that reliably makes this form
              taller than the viewport. Otherwise left unconstrained so
              in-flow popovers (calendar, dropdowns) that extend below the
              field triggering them are never clipped by an overflow
              boundary the base form never needed in the first place. */}
          <div
            className={`figma-modal-content${
              isSendForApprovalExpanded ? ' is-scrollable' : ''
            }`}
          >
            {/* Form Section */}
            <div
              ref={formSectionRef}
              onFocusCapture={() => {
                hasFormFocusRef.current = true;
              }}
              className={`figma-form-section${
                isApprovalReadOnly ? ' view-only' : ''
              }${
                // Gates the CSS "reopen for feedback" carve-out
                // (`.view-only.can-reopen-status [data-field='status']` in
                // EditMilestoneModal.css) that re-enables pointer-events on the
                // status field despite the blanket view-only lock. Without this
                // class, that carve-out fires for EVERY view-only state
                // (including Pending, where `canReopenStatusField` is false and
                // the status dropdown must stay fully inert) — see
                // `canReopenStatusField` in utils/approvalFlow.ts for why only
                // Approved/Re-Approved/Not-Approved may reopen.
                canReopenStatusField ? ' can-reopen-status' : ''
              }`}
            >
              {/* First Row - Core Milestone Information */}
              <div className="figma-form-row">
                <div className="figma-form-column">
                  {visibleFields.includes('type_of_review') && (
                    <div className="figma-form-field">
                      <label className="figma-field-label">
                        TYPE OF REVIEW
                        {isFieldRequired('type_of_review') ? ' *' : ''}
                      </label>
                      <div className="figma-type-dropdown-container">
                        <div
                          className="figma-type-dropdown-trigger"
                          onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setShowTypeDropdown(!showTypeDropdown);
                            } else if (e.key === 'Escape') {
                              setShowTypeDropdown(false);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-haspopup="listbox"
                          aria-expanded={showTypeDropdown}
                        >
                          <span className="figma-dropdown-text">
                            {formData.reviewType
                              ? getReviewTypeDisplayLabel(formData.reviewType)
                              : 'Select Type'}
                          </span>
                          <div className="figma-dropdown-chevron-container">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                            >
                              <path
                                d="M4.5 6.75L9 11.25L13.5 6.75"
                                stroke="#222222"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                        {showTypeDropdown && (
                          <div className="figma-type-dropdown-options">
                            <div className="figma-type-options-frame">
                              {MILESTONE_FORM_FIELDS.TYPE_OF_REVIEW.options
                                ?.filter(
                                  option =>
                                    !excludedReviewTypes.includes(option)
                                )
                                .map(option => {
                                  const isSelected =
                                    formData.reviewType === option;
                                  const displayLabel =
                                    getReviewTypeDisplayLabel(option);
                                  return (
                                    <div
                                      key={option}
                                      className="figma-type-option-row"
                                      onClick={() => {
                                        handleInputChange('reviewType', option);
                                        setShowTypeDropdown(false);
                                      }}
                                    >
                                      <div
                                        className={`figma-cpg-tag-button ${
                                          isSelected ? 'selected' : ''
                                        }`}
                                      >
                                        <div className="figma-cpg-container">
                                          <div className="figma-cpg-label">
                                            {displayLabel}
                                          </div>
                                        </div>
                                        {isSelected && (
                                          <div className="figma-check-icon">
                                            <BlackCheckMarkIcon
                                              width={18}
                                              height={18}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.reviewType && (
                        <div className="figma-error-message">
                          {errors.reviewType}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Date field - only show after status is selected */}
                  {shouldShowStatusDependentFields() &&
                    visibleFields.includes('date') && (
                      <div className="figma-form-field">
                        <DateComponent
                          field={{
                            id: 'date',
                            name: 'date',
                            label: getFieldLabel(
                              'date',
                              formData.status,
                              formData.reviewType
                            ),
                            type: 'datepicker',
                            required: true
                          }}
                          disableTBD={formData.status === 'meeting_scheduled'} // Disable TBD when time selection is required
                          disableNA={formData.status === 'meeting_scheduled'} // Disable N/A when time selection is required
                          // ⭐ For meeting_scheduled, always hide month/quarter - must be specific date
                          hideMonthList={
                            formData.status === 'meeting_scheduled'
                              ? true
                              : getCalendarRestrictions('date').hideMonthList
                          }
                          hideQuarterList={
                            formData.status === 'meeting_scheduled'
                              ? true
                              : getCalendarRestrictions('date').hideQuarterList
                          }
                          hideYearList={
                            getCalendarRestrictions('date').hideYearList
                          }
                          value={formData.date}
                          onChange={(fieldName, value) => {
                            // Extract the actual date string from the DateComponent's return value
                            let dateValue = value;

                            if (typeof value === 'object' && value !== null) {
                              if (value.date) {
                                // Handle {type: 'date', date: 'value'} format
                                dateValue = value.date;
                              } else if (
                                value.type === 'months' &&
                                Array.isArray(value.months) &&
                                value.months.length > 0
                              ) {
                                // Handle {type: 'months', months: ['Feb 2026']} format
                                dateValue = value.months[0];
                              } else if (
                                value.type === 'quarters' &&
                                Array.isArray(value.quarters) &&
                                value.quarters.length > 0
                              ) {
                                // Handle {type: 'quarters', quarters: ['Q1 2026']} format
                                dateValue = value.quarters[0];
                              } else if (
                                value.type === 'special' &&
                                value.value
                              ) {
                                // Handle {type: 'special', value: 'TBD'|'N/A'} format — keeps
                                // formData.date a plain string like every other status/date field.
                                dateValue = value.value;
                              }
                            }

                            handleInputChange('date', dateValue);
                          }}
                          validationState={errors.date ? 'invalid' : 'valid'}
                        />
                        {errors.date && (
                          <div className="figma-error-message">
                            {errors.date}
                          </div>
                        )}
                        {(() => {
                          const defaultDateValue = convertDateObjectToISO(
                            milestone?.date
                          )
                            .toString()
                            .trim();

                          return (
                            shouldShowSendTargetDefaultDateHelper({
                              isEdited: milestone?.isEdited,
                              reviewType: formData.reviewType,
                              status: formData.status,
                              defaultDateValue,
                              hasDateError: Boolean(errors.date)
                            }) && (
                              <div className="figma-helper-message">
                                {SEND_TARGET_DEFAULT_DATE_HELPER_MESSAGE}
                              </div>
                            )
                          );
                        })()}
                      </div>
                    )}

                  {/* Meeting scheduled date field */}
                  {shouldShowStatusDependentFields() &&
                    visibleFields.includes('meeting_scheduled_on') && (
                      <div className="figma-form-field">
                        <DateComponent
                          field={{
                            id: 'meetingScheduledOn',
                            name: 'meetingScheduledOn',
                            label: getFieldLabel(
                              'meeting_scheduled_on',
                              formData.status,
                              formData.reviewType
                            ),
                            type: 'datepicker',
                            required: true
                          }}
                          disableTBD={true} // Disable TBD when time selection is required
                          disableNA={true} // Disable N/A when time selection is required
                          // ⭐ ALWAYS hide month/quarter lists for meeting_scheduled_on - must be specific date
                          hideMonthList={true}
                          hideQuarterList={true}
                          hideYearList={
                            getCalendarRestrictions('meeting_scheduled_on')
                              .hideYearList
                          }
                          value={
                            formData.meetingScheduledOn
                              ? typeof formData.meetingScheduledOn === 'object'
                                ? formData.meetingScheduledOn
                                : ((): any => {
                                    try {
                                      const maybeObj = JSON.parse(
                                        String(formData.meetingScheduledOn)
                                      );
                                      if (
                                        maybeObj &&
                                        typeof maybeObj === 'object'
                                      )
                                        return maybeObj;
                                    } catch (_) {}
                                    return {
                                      type: 'date',
                                      date: String(formData.meetingScheduledOn)
                                    };
                                  })()
                              : ''
                          }
                          onChange={(fieldName, value) => {
                            // Extract the actual date string from the DateComponent's return value
                            let dateValue = value;

                            if (typeof value === 'object' && value !== null) {
                              if (value.date) {
                                // Handle {type: 'date', date: 'value'} format
                                dateValue = value.date;
                              } else if (
                                value.type === 'months' &&
                                Array.isArray(value.months) &&
                                value.months.length > 0
                              ) {
                                // Handle {type: 'months', months: ['Feb 2026']} format
                                dateValue = value.months[0];
                              } else if (
                                value.type === 'quarters' &&
                                Array.isArray(value.quarters) &&
                                value.quarters.length > 0
                              ) {
                                // Handle {type: 'quarters', quarters: ['Q1 2026']} format
                                dateValue = value.quarters[0];
                              }
                            }

                            handleInputChange('meetingScheduledOn', dateValue);
                          }}
                          validationState={
                            errors.meetingScheduledOn ? 'invalid' : 'valid'
                          }
                        />
                        {errors.meetingScheduledOn && (
                          <div className="figma-error-message">
                            {errors.meetingScheduledOn}
                          </div>
                        )}
                      </div>
                    )}
                </div>

                <div className="figma-form-column">
                  {visibleFields.includes('status') &&
                    statusOptions.length > 0 && (
                      <div className="figma-form-field" data-field="status">
                        <label className="figma-field-label">
                          STATUS
                          {isFieldRequired('status') ? ' *' : ''}
                        </label>
                        <div className="figma-type-dropdown-container">
                          <div
                            className="figma-type-dropdown-trigger"
                            onClick={() =>
                              setShowStatusDropdown(!showStatusDropdown)
                            }
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowStatusDropdown(!showStatusDropdown);
                              } else if (e.key === 'Escape') {
                                setShowStatusDropdown(false);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-haspopup="listbox"
                            aria-expanded={showStatusDropdown}
                          >
                            <span className="figma-dropdown-text">
                              {formData.status
                                ? getMilestoneStatusLabel(
                                    MILESTONE_STATUSES[
                                      (
                                        statusOptions.find(
                                          id =>
                                            id.toLowerCase() === formData.status
                                        ) ||
                                        // Legacy Async-approved records can have
                                        // a plain 'approved'/'re_approved'
                                        // status with no '_async' suffix (see
                                        // isCurrentStatusEquivalent below) —
                                        // fall back to a suffix-insensitive
                                        // match so this still resolves to a
                                        // label instead of "Select Status".
                                        statusOptions.find(
                                          id =>
                                            id
                                              .toLowerCase()
                                              .replace(/_async$/, '') ===
                                            (formData.status || '').replace(
                                              /_async$/,
                                              ''
                                            )
                                        )
                                      )?.toUpperCase() || ''
                                    ],
                                    isSentStatusPersisted
                                  ) || 'Select Status'
                                : 'Select Status'}
                            </span>
                            <div className="figma-dropdown-chevron-container">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                              >
                                <path
                                  d="M4.5 6.75L9 11.25L13.5 6.75"
                                  stroke="#222222"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </div>
                          {showStatusDropdown && (
                            <div className="figma-type-dropdown-options">
                              <div className="figma-type-options-frame">
                                {statusOptions
                                  .filter(
                                    statusId => MILESTONE_STATUSES[statusId]
                                  )
                                  .map(statusId => {
                                    const statusConfig =
                                      MILESTONE_STATUSES[statusId];
                                    // See `resolveStatusDropdownOption` in
                                    // utils/approvalFlow.ts for the full
                                    // enable/checked/action decision — while
                                    // reopening (canReopenStatusField), only
                                    // the current status (no-op), Sent
                                    // (handleReopenToSent), and the status
                                    // being reopened FROM
                                    // (handleUndoReopenToSent, a cancel) are
                                    // real options unless the persisted
                                    // outcome is Not Approved, which
                                    // enables every status.
                                    const {
                                      isCurrentStatusEquivalent,
                                      isOptionEnabled,
                                      action
                                    } = resolveStatusDropdownOption({
                                      statusId,
                                      formDataStatus: formData.status,
                                      persistedStatus: milestone?.status,
                                      canReopen: canReopenStatusField
                                    });
                                    return (
                                      <div
                                        key={statusId}
                                        className={`figma-type-option-row${
                                          isOptionEnabled
                                            ? ''
                                            : ' figma-status-option-disabled'
                                        }`}
                                        onClick={() => {
                                          if (!isOptionEnabled) return;
                                          if (action === 'reopenToSent') {
                                            handleReopenToSent();
                                          } else if (action === 'undoReopen') {
                                            handleUndoReopenToSent();
                                          } else {
                                            handleInputChange(
                                              'status',
                                              statusId.toLowerCase()
                                            );
                                          }
                                          setShowStatusDropdown(false);
                                        }}
                                      >
                                        <div
                                          className={`figma-cpg-tag-button ${
                                            isCurrentStatusEquivalent
                                              ? 'selected'
                                              : ''
                                          }`}
                                        >
                                          <div className="figma-cpg-container">
                                            <div className="figma-cpg-label">
                                              {getMilestoneStatusLabel(
                                                statusConfig,
                                                // Only the row matching the
                                                // currently active status
                                                // reflects whether it's
                                                // actually persisted; every
                                                // other row (incl. the
                                                // reopen-to-Sent action)
                                                // always represents an
                                                // unsaved pick.
                                                isCurrentStatusEquivalent
                                                  ? isSentStatusPersisted
                                                  : false
                                              )}
                                            </div>
                                          </div>
                                          {isCurrentStatusEquivalent && (
                                            <div className="figma-check-icon">
                                              <BlackCheckMarkIcon
                                                width={18}
                                                height={18}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}
                        </div>
                        {errors.status && (
                          <div className="figma-error-message">
                            {errors.status}
                          </div>
                        )}
                      </div>
                    )}

                  {shouldShowStatusDependentFields() &&
                    visibleFields.includes('meeting_scheduled_at') && (
                      <div className="figma-form-field">
                        <label className="figma-field-label">
                          {getFieldLabel(
                            'meeting_scheduled_at',
                            formData.status,
                            formData.reviewType
                          )}
                          {isFieldRequired('meeting_scheduled_at') ? ' *' : ''}
                        </label>
                        <div className="figma-time-range">
                          <div className="figma-type-dropdown-container">
                            <div
                              className="figma-timezone-selector"
                              onClick={() =>
                                setShowStartTimeDropdown(!showStartTimeDropdown)
                              }
                              style={{
                                borderColor: errors.meetingStartTime
                                  ? '#d32f2f'
                                  : undefined
                              }}
                            >
                              <span>{meetingStartTime || 'Start'}</span>
                              <div className="figma-dropdown-chevron-container">
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 18 18"
                                  fill="none"
                                >
                                  <path
                                    d="M4.5 6.75L9 11.25L13.5 6.75"
                                    stroke="#222222"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>
                            {showStartTimeDropdown && (
                              <div className="figma-timezone-dropdown-options">
                                {timeOptions.map(time => {
                                  const isSelected =
                                    meetingStartTime === time.value;
                                  return (
                                    <div
                                      key={time.value}
                                      className={`figma-dropdown-option ${
                                        isSelected ? 'selected' : ''
                                      }`}
                                      onClick={() => {
                                        setMeetingStartTime(time.value);
                                        setShowStartTimeDropdown(false);
                                        const startDisp = time.value;
                                        let endDisp = meetingEndTime || '';

                                        // Clear end time if it's before or equal to new start time
                                        if (
                                          endDisp &&
                                          convert12to24(endDisp) <=
                                            convert12to24(time.value)
                                        ) {
                                          endDisp = '';
                                          setMeetingEndTime('');
                                        }

                                        handleInputChange(
                                          'meetingScheduledAt',
                                          endDisp
                                            ? `${startDisp} - ${endDisp}`
                                            : startDisp
                                        );
                                      }}
                                    >
                                      <div>{time.label}</div>
                                      {isSelected && (
                                        <div className="figma-check-icon">
                                          <BlackCheckMarkIcon
                                            width={14}
                                            height={14}
                                            fill="#0265dc"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <span className="sep">-</span>
                          <div className="figma-type-dropdown-container">
                            <div
                              className="figma-timezone-selector"
                              onClick={() =>
                                setShowEndTimeDropdown(!showEndTimeDropdown)
                              }
                              style={{
                                borderColor: errors.meetingEndTime
                                  ? '#d32f2f'
                                  : undefined
                              }}
                            >
                              <span>{meetingEndTime || 'End'}</span>
                              <div className="figma-dropdown-chevron-container">
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 18 18"
                                  fill="none"
                                >
                                  <path
                                    d="M4.5 6.75L9 11.25L13.5 6.75"
                                    stroke="#222222"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>
                            {showEndTimeDropdown && (
                              <div className="figma-timezone-dropdown-options">
                                {timeOptions.map(time => {
                                  const isSelected =
                                    meetingEndTime === time.value;

                                  // Disable times that are before or equal to start time
                                  const isDisabled =
                                    meetingStartTime &&
                                    convert12to24(time.value) <=
                                      convert12to24(meetingStartTime);

                                  return (
                                    <div
                                      key={time.value}
                                      className={`figma-dropdown-option ${
                                        isSelected ? 'selected' : ''
                                      } ${isDisabled ? 'disabled' : ''}`}
                                      onClick={() => {
                                        if (isDisabled) return; // Prevent selection of disabled times
                                        setMeetingEndTime(time.value);
                                        setShowEndTimeDropdown(false);
                                        const startDisp =
                                          meetingStartTime || '';
                                        const endDisp = time.value;
                                        handleInputChange(
                                          'meetingScheduledAt',
                                          startDisp
                                            ? `${startDisp} - ${endDisp}`
                                            : endDisp
                                        );
                                      }}
                                      style={{
                                        opacity: isDisabled ? 0.4 : 1,
                                        cursor: isDisabled
                                          ? 'not-allowed'
                                          : 'pointer',
                                        pointerEvents: isDisabled
                                          ? 'none'
                                          : 'auto'
                                      }}
                                    >
                                      <div>{time.label}</div>
                                      {isSelected && !isDisabled && (
                                        <div className="figma-check-icon">
                                          <BlackCheckMarkIcon
                                            width={14}
                                            height={14}
                                            fill="#0265dc"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <span className="timezone-label">PST</span>
                        </div>
                        {errors.meetingScheduledAt && (
                          <div className="figma-error-message">
                            {errors.meetingScheduledAt}
                          </div>
                        )}
                        {errors.meetingStartTime && (
                          <div className="figma-error-message">
                            {errors.meetingStartTime}
                          </div>
                        )}
                        {errors.meetingEndTime && (
                          <div className="figma-error-message">
                            {errors.meetingEndTime}
                          </div>
                        )}
                      </div>
                    )}

                  {/* Secondary date field - NEEDS ASYNC FOLLOW-UP TARGET - only show after status is selected */}
                  {shouldShowStatusDependentFields() &&
                    visibleFields.includes('secondary_date') && (
                      <div className="figma-form-field">
                        <DateComponent
                          field={{
                            id: 'secondaryDate',
                            name: 'secondaryDate',
                            label: getEnhancedDynamicFieldLabel(
                              'secondary_date',
                              formData.status
                            ),
                            type: 'datepicker',
                            required: true
                          }}
                          value={formData.secondaryDate}
                          onChange={(fieldName, value) => {
                            // Extract the actual date string from the DateComponent's return value
                            let dateValue = value;

                            if (typeof value === 'object' && value !== null) {
                              if (value.date) {
                                // Handle {type: 'date', date: 'value'} format
                                dateValue = value.date;
                              } else if (
                                value.type === 'months' &&
                                Array.isArray(value.months) &&
                                value.months.length > 0
                              ) {
                                // Handle {type: 'months', months: ['Feb 2026']} format
                                dateValue = value.months[0];
                              } else if (
                                value.type === 'quarters' &&
                                Array.isArray(value.quarters) &&
                                value.quarters.length > 0
                              ) {
                                // Handle {type: 'quarters', quarters: ['Q1 2026']} format
                                dateValue = value.quarters[0];
                              } else if (
                                value.type === 'special' &&
                                value.value
                              ) {
                                // Handle {type: 'special', value: 'TBD'|'N/A'} format — without
                                // this, formData.secondaryDate stays an object, which crashes
                                // buildSlcApprovalSubject's (value || '').trim() the next render.
                                dateValue = value.value;
                              }
                            }

                            handleInputChange('secondaryDate', dateValue);
                          }}
                          validationState={
                            errors.secondaryDate ? 'invalid' : 'valid'
                          }
                        />
                        {errors.secondaryDate && (
                          <div className="figma-error-message">
                            {errors.secondaryDate}
                          </div>
                        )}
                      </div>
                    )}
                </div>
              </div>

              {/* Separator Line - Only show when there are additional fields */}
              {shouldShowLinkFields() && (
                <div className="figma-separator"></div>
              )}

              {/* Second Row - Content Links Section. Flows Document Link,
                  Recording Link, Meeting Notes, and (when the Send-for-
                  Approval flow is active) Approver through ONE continuous
                  2-column grid, in that fixed order, skipping whichever are
                  hidden/absent — then chunks the survivors into rows of 2.
                  So e.g. if Document Link is hidden (MVP always hides it),
                  Recording Link and Meeting Notes compact onto the same row
                  together instead of each getting a half-empty row of their
                  own; if all four are present, it's still exactly the
                  original 2x2 (Document Link+Recording Link, then Meeting
                  Notes+Approver). This is what keeps it generic: one ordered
                  list + chunk-by-2, not a rule keyed to any specific field's
                  name/relation to another. */}
              {shouldShowLinkFields() &&
                (() => {
                  const showApproverHere =
                    isApproverVisible && approverMergesIntoLinksRow;
                  const fields = [
                    !hiddenFields.includes('document_link') && (
                      <div className="figma-form-field" key="document_link">
                        <label className="figma-field-label">
                          DOCUMENT LINK
                          {isFieldRequired('document_link') ? ' *' : ''}
                        </label>
                        <input
                          type="url"
                          className="figma-text-field"
                          value={formData.documentLink}
                          onChange={e =>
                            handleInputChange('documentLink', e.target.value)
                          }
                          onBlur={() => markFieldTouched('documentLink')}
                          placeholder="Enter URL"
                          style={{
                            borderColor: errors.documentLink
                              ? '#d32f2f'
                              : undefined
                          }}
                        />
                        {/* Individual error messages removed - consolidated below */}
                      </div>
                    ),
                    <div className="figma-form-field" key="recording_link">
                      <label className="figma-field-label">
                        RECORDING LINK
                        {isFieldRequired('recording_link') ? ' *' : ''}
                      </label>
                      <input
                        type="url"
                        className="figma-text-field"
                        value={formData.recordingLink}
                        onChange={e =>
                          handleInputChange('recordingLink', e.target.value)
                        }
                        onBlur={() => markFieldTouched('recordingLink')}
                        placeholder="Enter URL"
                        style={{
                          borderColor: errors.recordingLink
                            ? '#d32f2f'
                            : undefined
                        }}
                      />
                      {/* Individual error messages removed - consolidated below */}
                    </div>,
                    visibleFields.includes('notes_link') && (
                      <div className="figma-form-field" key="notes_link">
                        <label className="figma-field-label">
                          MEETING NOTES
                          {isFieldRequired('notes_link') ? ' *' : ''}
                        </label>
                        <input
                          type="url"
                          className="figma-text-field"
                          value={formData.notesLink}
                          onChange={e =>
                            handleInputChange('notesLink', e.target.value)
                          }
                          onBlur={() => markFieldTouched('notesLink')}
                          placeholder="Enter URL"
                          style={{
                            borderColor: errors.notesLink
                              ? '#d32f2f'
                              : undefined
                          }}
                        />
                        {/* Individual error messages removed - consolidated below */}
                      </div>
                    ),
                    showApproverHere && (
                      <div className="figma-form-field" key="approver">
                        {renderApproverFieldContent()}
                      </div>
                    )
                  ].filter(Boolean);

                  const rows: (typeof fields)[] = [];
                  for (let i = 0; i < fields.length; i += 2) {
                    rows.push(fields.slice(i, i + 2));
                  }

                  return (
                    <>
                      {rows.map((rowFields, rowIndex) => (
                        <div className="figma-form-row" key={rowIndex}>
                          <div className="figma-form-column">
                            {rowFields[0]}
                          </div>
                          <div className="figma-form-column">
                            {rowFields[1]}
                          </div>
                        </div>
                      ))}

                      {/* Consolidated error message for all link fields - only show errors for visible fields.
                          Always rendered (never mounted/unmounted) so this reserves its height at all
                          times — otherwise, since live validation re-runs on every keystroke once a field
                          is touched, the message flickers in/out as URL validity toggles while typing,
                          shifting the whole form up/down on each keystroke. */}
                      <div className="figma-links-section-error">
                        {getConsolidatedLinkFieldError()}
                      </div>
                    </>
                  );
                })()}

              {/* Approver - dedicated section: Meeting review type, or Async
                  without Send for Approval checked (approverMergesIntoLinksRow
                  false), or no link fields shown at all. */}
              {isApproverVisible && !approverMergesIntoLinksRow && (
                <>
                  <div className="figma-separator"></div>
                  <div className="figma-form-row">
                    <div className="figma-form-column">
                      <div className="figma-form-field">
                        {renderApproverFieldContent()}
                      </div>
                    </div>
                    <div className="figma-form-column">
                      {/* Empty column for layout balance */}
                    </div>
                  </div>
                </>
              )}

              {/* Re-Approval Historical Data Section */}
              {((visibleFields.includes('original_approved_on') &&
                !hiddenFields.includes('original_approved_on')) ||
                (visibleFields.includes('re_approved_on') &&
                  !hiddenFields.includes('re_approved_on'))) && (
                <>
                  <div className="figma-separator"></div>
                  <div className="figma-form-row">
                    <div className="figma-form-column">
                      {visibleFields.includes('original_approved_on') &&
                        !hiddenFields.includes('original_approved_on') && (
                          <div className="figma-form-field">
                            <label className="figma-field-label">
                              ORIGINAL APPROVED ON*
                            </label>
                            <input
                              type="date"
                              className="figma-date-input"
                              value={formData.originalApprovedOn || ''}
                              readOnly
                              style={{
                                backgroundColor: '#f5f5f5',
                                color: '#666666',
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                        )}
                    </div>

                    <div className="figma-form-column">
                      {visibleFields.includes('re_approved_on') &&
                        !hiddenFields.includes('re_approved_on') && (
                          <div className="figma-form-field">
                            <label className="figma-field-label">
                              RE-APPROVED ON*
                            </label>
                            <input
                              type="date"
                              className="figma-date-input"
                              value={formData.reApprovedOn || ''}
                              readOnly
                              style={{
                                backgroundColor: '#f5f5f5',
                                color: '#666666',
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                        )}
                    </div>
                  </div>
                </>
              )}

              {/* Approver Details Section */}
              {((visibleFields.includes('approved_by') &&
                !hiddenFields.includes('approved_by')) ||
                (visibleFields.includes('re_approved_by') &&
                  !hiddenFields.includes('re_approved_by'))) && (
                <>
                  <div className="figma-separator"></div>
                  <div className="figma-form-row">
                    <div className="figma-form-column">
                      {visibleFields.includes('approved_by') &&
                        !hiddenFields.includes('approved_by') && (
                          <div className="figma-form-field">
                            <label className="figma-field-label">
                              APPROVER
                            </label>
                            <div className="figma-picker-container">
                              <select
                                className="figma-picker"
                                value={formData.approvedBy || ''}
                                disabled
                                style={{
                                  backgroundColor: '#f5f5f5',
                                  color: '#666666'
                                }}
                              >
                                <option value={formData.approvedBy || ''}>
                                  {formData.approvedBy || 'Not Set'}
                                </option>
                              </select>
                              <div className="figma-dropdown-chevron-container">
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 18 18"
                                  fill="none"
                                >
                                  <path
                                    d="M4.5 6.75L9 11.25L13.5 6.75"
                                    stroke="#666666"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>

                    <div className="figma-form-column">
                      {visibleFields.includes('re_approved_by') &&
                        !hiddenFields.includes('re_approved_by') && (
                          <div className="figma-form-field">
                            <label className="figma-field-label">
                              RE-APPROVER
                            </label>
                            <div className="figma-picker-container">
                              <select
                                className="figma-picker"
                                value={formData.reApprovedBy || ''}
                                disabled
                                style={{
                                  backgroundColor: '#f5f5f5',
                                  color: '#666666'
                                }}
                              >
                                <option value={formData.reApprovedBy || ''}>
                                  {formData.reApprovedBy || 'Not Set'}
                                </option>
                              </select>
                              <div className="figma-dropdown-chevron-container">
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 18 18"
                                  fill="none"
                                >
                                  <path
                                    d="M4.5 6.75L9 11.25L13.5 6.75"
                                    stroke="#666666"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </>
              )}

              {/* Send for Approval — Async milestones in an approval-sending status (e.g. Sent) */}
              {showSendForApproval && (
                <SendForApprovalForm
                  readOnly={!isSentStatus}
                  disabled={!isSentStatus || !isPreviousFormValid}
                  sendForApproval={formData.sendForApproval}
                  onSendForApprovalChange={checked =>
                    handleInputChange('sendForApproval', checked)
                  }
                  approvers={getApproverContacts()}
                  slcTeamCc={slcTeamCc}
                  cc={formData.cc}
                  onCcChange={value => handleInputChange('cc', value)}
                  subject={sendForApprovalSubject}
                  note={formData.noteToApprover}
                  onNoteChange={value =>
                    handleInputChange('noteToApprover', value)
                  }
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="figma-modal-footer">
            <button
              className="figma-button-secondary"
              onClick={handleCancelClick}
            >
              Cancel
            </button>
            <span className="figma-primary-button-wrapper">
              <button
                className="figma-button-primary"
                onClick={handleSave}
                disabled={isApprovalReadOnly || isSaving}
              >
                {showSendForApproval && formData.sendForApproval
                  ? 'Save and Send'
                  : 'Save'}
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>,
    // document.body
    document.querySelector('[data-testid="slideout-panel"]')!
  );
};
